// InvoiceManagement.jsx — landing for the Invoice Management package.
// Lives at /invoice-management/:tab? — deep-linkable. Redesigned to match the
// Chat / Email Management surfaces: a tab strip, a shared brand+date filter bar
// on top, and a board or table per tab. Create + reminder flows live in modals
// launched from the header "+" button.
//
//   1. invoices   — Kanban or List, the same switch Projects has. TWO ways in,
//                   both per brand and both equal from here on:
//                     "+ New invoice"    build it from line items and compile;
//                     "Upload invoice"   you already have the PDF.
//                   Either one can be reminded on, and both appear in the same
//                   per-brand payout figures.
//   2. reminders  — the loops. Every flow is created SILENT and sends nothing
//                   until its toggle is switched on.
//   3. analytics  — money due / recovered, reminders + responses, time-to-pay,
//                   and payout per brand.
//
// TWO SWITCHES GOVERN WHETHER A CLIENT IS EMAILED, and they are deliberately
// different things:
//   * the reminder's SENDING toggle — off by default; turning it on sends.
//   * the invoice's PAID checkbox   — the hard stop. It completes every flow
//     chasing that invoice and blocks all future email about it, from any flow.
//
// Route is gated by INVOICE_VIEW in App.jsx; per-tab visibility + the action
// buttons additionally check the matching invoice permission.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  listInvoices, createInvoice, updateInvoice, generateInvoice, uploadInvoice, setInvoicePaid,
  listReminders, createReminder, confirmPayment, setReminderSending, archiveReminder,
  listReminderMailboxes, createPaymentLink, getPaymentLinkStatus, getInvoice, archiveInvoice,
  listBankAccounts, getBrandAddress,
  getInvoiceAnalytics, listClients, fetchInvoiceDocument,
} from "../../api/invoices/invoices";
import { get } from "../../api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import SystemFilterBar from "components/SystemFilterBar";
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from "../../helpers/systemFilters";
import useBrandList from "../../hooks/useBrandList";
import { fieldSetter } from "../../helpers/formEvents";
import ClientsTab from "./ClientsTab";
import BankAccountsTab, { accountLabel } from "./BankAccountsTab";
import TimeTab from "./TimeTab";
import EmailSetupTab from "./EmailSetupTab";
import ReminderPreview from "./ReminderPreview";
import DocumentViewer from "./DocumentViewer";
import {
  startIsPending, reminderSendState,
  browserTimeZone, timeZoneOptions, zonedInputToIso, toZonedInputValue, fmtInZone,
} from "./scheduleTime";
import FromTimeEntriesModal from "./FromTimeEntriesModal";

// THE CURRENCIES AN INVOICE MAY BE IN.
//
// A free-text box let "$" through, and a currency symbol is not a currency
// code: Stripe rejected it, the payment link silently failed to exist, and
// nothing on screen said why. A fixed list cannot produce that.
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'AED', 'SAR', 'INR', 'JPY'];

const CurrencyPicker = ({ value, onChange, style }) => (
  <select className="form-control" value={value} onChange={(e) => onChange(e.target.value)} style={style} title="ISO currency code">
    {/* An invoice already saved with something odd keeps showing it rather than
        silently becoming dollars the next time somebody opens the form. */}
    {!CURRENCIES.includes(value) && value ? <option value={value}>{value} (not a currency code)</option> : null}
    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
  </select>
);

const TABS = [
  { key: "invoices",  label: "Invoices",  perm: "INVOICE_VIEW" },
  { key: "clients",   label: "Clients",   perm: "CLIENT_VIEW" },
  // The accounts a brand bills FROM, kept as a named list so an invoice picks
  // one instead of having a routing number retyped onto it from memory.
  { key: "banking",   label: "Bank accounts", perm: "INVOICE_VIEW" },
  { key: "time",      label: "Time",      perm: "TIME_ENTRY_VIEW" },
  { key: "reminders", label: "Reminders", perm: "INVOICE_REMINDER_VIEW" },
  // Archived flows: off the board, still on the record. Reminders is the
  // working set; this is everything put away.
  { key: "archive",   label: "Archive",   perm: "INVOICE_REMINDER_VIEW" },
  { key: "analytics", label: "Analytics", perm: "INVOICE_ANALYTICS_VIEW" },
  { key: "email",     label: "Email setup", perm: "INVOICE_REMINDER_VIEW" },
];

// Tabs that own their data, their filters and their own empty states.
const SELF_CONTAINED = new Set(["clients", "banking", "time", "analytics", "email"]);

// The board's columns. Paid is last and is the only one an operator moves a
// card INTO by hand — the others are what the system did to the invoice.
export const INVOICE_COLUMNS = [
  { key: 'draft',     label: 'Draft',     hint: 'not compiled yet' },
  { key: 'generated', label: 'Ready',     hint: 'document exists, nothing sent' },
  { key: 'sent',      label: 'Chasing',   hint: 'a reminder has gone out' },
  { key: 'overdue',   label: 'Overdue',   hint: 'past its due date' },
  { key: 'paid',      label: 'Paid',      hint: 'settled — nothing more is emailed' },
];

const COLUMN_TONE = {
  draft:     { bg: '#f1f2f4', fg: '#5b6670' },
  generated: { bg: '#eef2ff', fg: '#15172b' },
  sent:      { bg: '#fff4cc', fg: '#856404' },
  overdue:   { bg: '#fdecea', fg: '#a71d2a' },
  paid:      { bg: '#e7f5ec', fg: '#1e6b3e' },
  cancelled: { bg: '#fafbfc', fg: '#888' },
};

const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) { try { return new Date(v).toLocaleString(); } catch {} }
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 60) + '…';
  return String(v);
}
function fmtMoney(v, currency) { return `${currency || ''} ${round2(v)}`.trim(); }
// A DUE DATE IS A DAY, AND IT IS SHOWN AS THE DAY THAT WAS TYPED.
//
// The date box sends "2026-04-30" and it is stored as UTC midnight on that day.
// Rendering that with a plain `toLocaleDateString()` formats it in the
// BROWSER's timezone, where UTC midnight is the previous evening — so an
// operator anywhere west of UTC typed 30 April, saved it, and the row read 29
// April. Nothing was wrong except everything they could see.
//
// Formatting in UTC gives back the day that went in, on every machine. The
// other half of this rule is server-side, in services/invoiceDueDate.
export function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return String(v); }
}

// THE PERIOD THE INVOICE COVERS, as one cell.
//
// `sentAt` says when it went out and `dueDate` says when it has to be paid;
// neither answers "which month is this one for?", and for a retainer raised on
// the 3rd for the previous month that is the first thing anybody asks. Both
// ends are inclusive and both are formatted in UTC for the same reason fmtDate
// is — they are days, not moments.
//
// An invoice with no period gets a dash rather than an invented one: most
// one-off invoices do not cover a span, and every invoice raised before the
// field existed has none. A half-stated period prints the end it knows with the
// other side left open, because "from 1 Aug" is true and useful.
export function fmtPeriod(inv) {
  const a = inv && inv.periodStart ? fmtDate(inv.periodStart) : '';
  const b = inv && inv.periodEnd ? fmtDate(inv.periodEnd) : '';
  if (!a && !b) return '—';
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a ? `${a} – …` : `… – ${b}`;
}

/**
 * Which column an invoice belongs in.
 *
 * Paid wins over everything, and it is read from the `paid` flag first: that
 * field is the one the checkbox writes and the one the worker checks before
 * sending, so the board must agree with it rather than with the status label.
 * Overdue is derived here rather than stored, so a due date passing moves the
 * card without anything having to run.
 */
export function invoiceColumn(inv) {
  if (!inv) return 'draft';
  if (inv.paid === true || inv.status === 'paid') return 'paid';
  if (inv.status === 'cancelled') return 'cancelled';
  if (inv.dueDate && new Date(inv.dueDate).getTime() < Date.now()) return 'overdue';
  if (inv.status === 'sent' || inv.usedForReminding) return 'sent';
  if (inv.status === 'generated') return 'generated';
  return 'draft';
}

// Client-side preview that mirrors back/services/invoiceMath.
function previewTotals(lineItems, applyTax, taxRate) {
  const subtotal = round2(
    (lineItems || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0)
  );
  const rate = applyTax ? Number(taxRate) || 0 : 0;
  const taxAmount = round2(subtotal * (rate / 100));
  return { subtotal, taxAmount, total: round2(subtotal + taxAmount) };
}

// Build the attachment payload from a file's data-uri. Pure + exported so the
// upload behaviour is unit-testable without the DOM. Returns null for a falsy
// data-uri; throws when the data-uri is not a base64 PDF.
export function attachmentFromDataUri(dataUri, filename) {
  if (!dataUri) return null;
  if (!/^data:application\/pdf;base64,/i.test(dataUri)) {
    throw new Error('Please choose a PDF file.');
  }
  const name = filename && /\.pdf$/i.test(filename) ? filename : `${filename || 'invoice'}.pdf`;
  return { filename: name, contentType: 'application/pdf', dataUri };
}

function readFileAsDataUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// ────────────────────────────────────────────────────────────── Modal
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: wide ? 760 : 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0" style={{ wordBreak: 'break-word', paddingRight: 12 }}>{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Brand + client pickers, shared by both ways of creating an invoice so the two
// paths cannot drift on the thing that scopes everything downstream.
function PartyPickers({ brands, clients, brandId, setBrandId, clientId, setClientId }) {
  const forBrand = useMemo(() => (
    (clients || []).filter((c) => !brandId || String(c.brand_id || '') === String(brandId) || !c.brand_id)
  ), [clients, brandId]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <label style={{ fontSize: 12, color: '#666' }}>Brand *
        <select className="form-control" value={brandId} onChange={(e) => { setBrandId(e.target.value); setClientId(''); }}>
          <option value="">Pick a brand…</option>
          {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
        </select>
      </label>
      <label style={{ fontSize: 12, color: '#666' }}>Client
        <select className="form-control" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">No client on file</option>
          {forBrand.map((c) => (<option key={c._id} value={c._id}>{c.name}{c.email ? ` — ${c.email}` : ''}</option>))}
        </select>
      </label>
    </div>
  );
}

// THE PROJECT / JOB THE INVOICE BILLS AGAINST (2026-10-15, the user).
//
// Picked from the client's own projects when it has any, so the job on the
// invoice is the same one its hours were logged against — and the server
// copies that project's name onto the invoice, which is what the PDF prints
// and every reminder names. "Other" still lets a one-off name be typed, and a
// client with no projects gets the plain box it always had.
export const PROJECT_OTHER = '__other__';

// The client an invoice's projects come from: the one picked on the form, or
// on an edit the client on file whose address or name the invoice carries.
export function clientForInvoice(clients, clientId, party) {
  const list = clients || [];
  const picked = list.find((c) => String(c._id) === String(clientId));
  if (picked || !party) return picked || null;
  const email = String(party.email || '').trim().toLowerCase();
  const name = String(party.name || '').trim().toLowerCase();
  return list.find((c) => email && String(c.email || '').toLowerCase() === email)
    || list.find((c) => name && String(c.name || '').trim().toLowerCase() === name)
    || null;
}

// WHAT THE TWO ADDRESS BLOCKS WILL PRINT, shown on the form (the user,
// 2026-10-15). Both were only visible on the compiled PDF, which is how an
// invoice went out "Billed To" the brand's own address. Same precedence as the
// save (client picked, else what the invoice carries) and as the template
// (the invoice's own From address, else the brand's).
export function partyLine(name, address, country) {
  return [name, address, country].map((v) => String(v || '').trim()).filter(Boolean).join(', ');
}
export function billedToPreview(client, keep, clientName, clientCountry) {
  const k = keep || {};
  return partyLine(clientName, (client && client.address) || k.address, clientCountry);
}
export function fromPreview(brandParty, brandName, brandAddress) {
  const p = brandParty || {};
  return partyLine(p.name || brandName, String(p.address || '').trim() || brandAddress, p.country);
}

// Its own component: the invoice form is at the size where one more branch in
// its JSX trips eslint-plugin-react-hooks' path-count overflow, which then
// reports every hook in the form as "called conditionally".
function AddressPreview({ from, brandId, brandAddress, brandParty, billedTo }) {
  const noBrandAddress = !!brandId && !String(brandAddress || '').trim()
    && !String((brandParty && brandParty.address) || '').trim();
  return (
    <div style={{ fontSize: 12, color: '#525252', marginTop: 6, lineHeight: 1.5 }}>
      <div data-testid="from-preview">
        <b>From:</b> {from || '—'}
        {noBrandAddress && (
          <span style={{ color: '#b45309' }}> — this brand has no address. Set it under Bank accounts → Invoice "From" address.</span>
        )}
      </div>
      <div data-testid="billed-to-preview"><b>Billed to:</b> {billedTo || '—'}</div>
    </div>
  );
}

export function ProjectPicker({ client, projectId, setProjectId, projectName, setProjectName }) {
  const projects = ((client && client.projects) || []).filter((p) => p.status !== 'archived' || String(p._id) === String(projectId));
  const linked = projects.find((p) => String(p._id) === String(projectId));
  const [other, setOther] = useState(!linked && !!String(projectName || '').trim());
  // A project left over from another client is not this client's job.
  const all = (client && client.projects) || [];
  useEffect(() => {
    if (client && projectId && !all.some((p) => String(p._id) === String(projectId))) {
      setProjectId('');
      setProjectName('');
    }
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!projects.length) {
    return (
      <input className="form-control" aria-label="Project" value={projectName}
        onChange={(e) => { setProjectId(''); setProjectName(e.target.value); }} placeholder="e.g. Riverside Medical Center" />
    );
  }
  const value = linked ? String(linked._id) : (other ? PROJECT_OTHER : '');
  return (
    <>
      <select className="form-control" aria-label="Project" value={value} onChange={(e) => {
        const v = e.target.value;
        if (v === PROJECT_OTHER) { setOther(true); setProjectId(''); return; }
        setOther(false);
        const p = projects.find((x) => String(x._id) === v);
        setProjectId(p ? String(p._id) : '');
        setProjectName(p ? p.name : '');
      }}>
        <option value="">No project</option>
        {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        <option value={PROJECT_OTHER}>Other — type a name</option>
      </select>
      {value === PROJECT_OTHER && (
        <input className="form-control mt-1" aria-label="Project name" value={projectName}
          onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. Riverside Medical Center" />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────── Create invoice
// THE SAME FORM WRITES AN INVOICE AND CORRECTS ONE.
//
// Everything an invoice carries was settable exactly once, at creation: a typo
// in the client's name, a rate that changed, a due date agreed after the fact,
// an ACH line that moved bank — all of it meant deleting the invoice and
// writing it again, which loses the number, the reminder loop and the record of
// what was billed and when. `PUT /api/invoices/:id` existed the whole time and
// nothing in the product called it.
//
// One component rather than two, because an edit form that drifts from the
// create form is how a field ends up settable in one place and not the other —
// which is the bug this fixes, one level up.
export function CreateInvoiceModal({ brands, clients, invoice, accounts, accountsErr, brandDefaults, onClose, onSaved, onRefresh }) {
  const editing = !!(invoice && invoice._id);
  // An UPLOADED invoice has no line items to add up: the operator's figure IS
  // the total, and the document is the PDF they handed us. Its money is edited
  // as one number and it is never recompiled — compiling would write an empty
  // line-item invoice over the document that already exists.
  const uploaded = editing && invoice.source === 'uploaded';

  const [invoiceNumber, setInvoiceNumber] = useState((invoice && invoice.invoiceNumber) || "");
  const [brandId, setBrandId] = useState((invoice && invoice.brand_id) || "");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState((invoice && invoice.clientParty && invoice.clientParty.name) || "");
  const [clientCountry, setClientCountry] = useState((invoice && invoice.clientParty && invoice.clientParty.country) || "");
  // The stored day, back in the date box that wrote it. It is UTC midnight on
  // that day, so the first ten characters ARE the day — no timezone applies.
  const [dueDate, setDueDate] = useState(
    invoice && invoice.dueDate ? String(new Date(invoice.dueDate).toISOString()).slice(0, 10) : "",
  );
  // THE INVOICE DATE — the day it was raised, printed on the document and
  // checked in every reminder that states it. Same day-in-a-box rule.
  const [issuedAt, setIssuedAt] = useState(
    invoice && invoice.issuedAt ? String(new Date(invoice.issuedAt).toISOString()).slice(0, 10) : "",
  );
  // THE EFFECTIVE DATES — the span of work this invoice bills for, which is
  // neither the day it was raised nor the day it falls due. Same day-in-a-box
  // handling as the due date above: the stored value is UTC midnight on the
  // day, so its first ten characters ARE the day the operator typed.
  const [periodStart, setPeriodStart] = useState(
    invoice && invoice.periodStart ? String(new Date(invoice.periodStart).toISOString()).slice(0, 10) : "",
  );
  const [periodEnd, setPeriodEnd] = useState(
    invoice && invoice.periodEnd ? String(new Date(invoice.periodEnd).toISOString()).slice(0, 10) : "",
  );
  const [currency, setCurrency] = useState((invoice && invoice.currency) || "USD");
  // Construction pay-app facts, read by the Trade contractor reminder ladder.
  const [projectName, setProjectName] = useState((invoice && invoice.projectName) || "");
  const [projectId, setProjectId] = useState((invoice && invoice.project_id && String(invoice.project_id)) || "");
  const [payAppNumber, setPayAppNumber] = useState((invoice && invoice.payAppNumber) || "");
  const [retainage, setRetainage] = useState(invoice && Number(invoice.retainage) > 0 ? String(invoice.retainage) : "");
  // Which reminder ladder chases it — its loop follows this.
  const [reminderLadder, setReminderLadder] = useState((invoice && invoice.reminderLadder) || "standard");
  // The invoice's own mark, used at the TOP LEFT when the brand has no logo —
  // never at the foot, where it printed until 2026-09-08 and read as a
  // signature. The state keeps the schema's old name so nothing on the record
  // has to be rewritten; the label on the form says what it actually does.
  const [signatureImage, setSignatureImage] = useState((invoice && invoice.signatureImage) || "");
  const [applyTax, setApplyTax] = useState(!!(invoice && invoice.applyTax));
  const [taxRate, setTaxRate] = useState((invoice && invoice.taxRate) || 20);
  const [total, setTotal] = useState(invoice ? String(invoice.total == null ? '' : invoice.total) : '');
  const [lineItems, setLineItems] = useState(
    invoice && Array.isArray(invoice.lineItems) && invoice.lineItems.length
      ? invoice.lineItems.map((it) => ({
        description: it.description || '', quantity: it.quantity, unitPrice: it.unitPrice,
      }))
      : [{ description: "", quantity: 1, unitPrice: 0 }],
  );
  // ACH details ride on the invoice and are repeated in EVERY reminder, printed
  // from here rather than written by the drafter — a routing number a model
  // invented is worse than none at all.
  const [ach, setAch] = useState({
    achBankName: '', achAccountName: '', achAccountType: '', achRoutingNumber: '', achAccountNumber: '',
    method: '', extraLines: [],
    ...((invoice && invoice.paymentDetails) || {}),
  });
  // WHICH NAMED ACCOUNT THESE CAME FROM.
  //
  // Picking one COPIES its details into the boxes below, rather than storing a
  // reference and reading through it at print time. That is the whole design:
  // an invoice must keep saying what it said the day it was sent, so correcting
  // a routing number in the catalogue next month cannot silently change what a
  // client was told to pay into. This id is provenance — it lets the form
  // reopen showing which one was chosen, and nothing else depends on it.
  const [bankAccountId, setBankAccountId] = useState((invoice && invoice.bankAccount_id) || "");
  const [bankAccounts, setBankAccounts] = useState([]);
  // THE MAILBOX THIS INVOICE IS CHASED FROM. Decided here, where the brand and
  // the client are already on screen, rather than discovered later on the
  // reminder form — and the loop that opens with the invoice copies it, so the
  // address chosen while writing the invoice is the one the client hears from.
  const [accountId, setAccountId] = useState((invoice && invoice.account_id) || "");
  // Whether the operator has touched the picker. A brand change re-seeds the
  // default only until they choose for themselves; after that, changing the
  // brand must not overwrite a deliberate choice.
  const [accountTouched, setAccountTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // THE COMPILED INVOICE, SHOWN BEFORE THE MODAL CLOSES.
  //
  // "Create + Compile PDF" produced a document nobody saw: the modal closed and
  // the invoice appeared as a row. The first look at what a client will receive
  // was the client's. It is compiled here anyway — showing it costs one render
  // and is the only chance to catch a wrong address or a missing line before it
  // is attached to an email.
  const [preview, setPreview] = useState(null);

  const brand = (brands || []).find((b) => String(b._id || b.id) === String(brandId));
  const client = (clients || []).find((c) => String(c._id) === String(clientId));
  // Whose projects the Project picker offers — on an edit, the client on file.
  const projectClient = clientForInvoice(clients, clientId, invoice && invoice.clientParty);

  // ON AN EDIT THE CLIENT PICKER STARTS ON THE CLIENT ON FILE, not on "No
  // client on file" (the user, 2026-10-15): a picker that always read "none"
  // hid which client the invoice was tied to, and the wrong one was picked to
  // "fix" it. Seeded once, and WITHOUT the fill-in below — opening an invoice
  // must not rewrite its typed name or its currency.
  const seededClient = useRef(false);
  const skipClientFill = useRef(false);
  useEffect(() => {
    if (!editing || seededClient.current || !(clients || []).length) return;
    seededClient.current = true;
    const onFile = clientForInvoice(clients, '', invoice && invoice.clientParty);
    if (onFile) { skipClientFill.current = true; setClientId(String(onFile._id)); }
  }, [editing, clients, invoice]);

  // The brand's own address — what "From" prints when the invoice carries none.
  const [brandAddress, setBrandAddressState] = useState('');
  useEffect(() => {
    setBrandAddressState('');
    if (!brandId) return undefined;
    let live = true;
    try {
      getBrandAddress(brandId)
        .then((r) => { if (live) setBrandAddressState((r && r.data && r.data.mailingAddress) || ''); })
        .catch(() => {});
    } catch (_) { /* the line says no address, which is the safe read */ }
    return () => { live = false; };
  }, [brandId]);

  // The catalogue, loaded once. Fetched unfiltered and narrowed below rather
  // than refetched per brand: changing the brand in this form must not put the
  // picker into a loading state mid-edit.
  useEffect(() => {
    let live = true;
    // Wrapped, not just .catch()'d. A failure to load the catalogue must never
    // take the invoice form down with it — the boxes below still work by hand,
    // which is how every invoice was written before this list existed. A
    // .catch alone does not cover a synchronous throw from the call itself.
    try {
      listBankAccounts()
        .then((r) => { if (live) setBankAccounts((r && r.data) || []); })
        .catch(() => {});
    } catch (_) { /* no catalogue; type the details in */ }
    return () => { live = false; };
  }, []);

  // WHAT THIS BRAND MAY BILL FROM. Its own accounts and the company-wide ones —
  // never another brand's. Putting one brand's bank account on another brand's
  // invoice is the single mistake this whole area is built to prevent (see
  // services/invoiceTemplate: there is deliberately no shared default).
  const brandAccounts = useMemo(() => (bankAccounts || []).filter(
    (a) => !a.brand_id || !brandId || String(a.brand_id) === String(brandId),
  ), [bankAccounts, brandId]);

  // The mailboxes this brand may send from: its own first, everyone else's
  // under a divider. Chasing one brand's money from another brand's address is
  // a mismatch the client sees before we do — so the brand's own are the ones
  // offered, and the rest are reachable but visibly separate. Same rule and
  // same shape as the reminder form's picker.
  const ownMailboxes = useMemo(
    () => (accounts || []).filter((a) => brandId && String(a.brand_id || '') === String(brandId)),
    [accounts, brandId],
  );
  const otherMailboxes = useMemo(
    () => (accounts || []).filter((a) => !brandId || String(a.brand_id || '') !== String(brandId)),
    [accounts, brandId],
  );

  // WHAT THIS BRAND IS REMEMBERED TO BILL FROM. Picking a brand preselects it,
  // so the question is answered once per brand rather than once per invoice.
  // A mailbox belonging to another brand, deliberately allowed but flagged: it
  // is occasionally right (a shared accounts-receivable box) and is far more
  // often a mistake the client is the first to notice.
  const crossBrandMailbox = useMemo(() => {
    if (!accountId || !brandId) return false;
    const picked = (accounts || []).find((a) => String(a._id) === String(accountId));
    return !!picked && String(picked.brand_id || '') !== String(brandId);
  }, [accountId, brandId, accounts]);

  useEffect(() => {
    if (accountTouched || !brandId) return;
    // An invoice being EDITED already carries its answer; the remembered
    // default must not overwrite what was actually used.
    if (invoice && invoice.account_id) return;
    const remembered = (brandDefaults || {})[String(brandId)];
    if (remembered && (accounts || []).some((a) => String(a._id) === String(remembered))) {
      setAccountId(String(remembered));
    }
  }, [brandId, brandDefaults, accounts, accountTouched, invoice]);

  // Copy an account's details into the boxes. A COPY, not a link — see the
  // comment on bankAccountId above.
  const applyBankAccount = useCallback((acct) => {
    if (!acct) return;
    setAch((prev) => ({
      ...prev,
      achBankName: acct.bankName || '',
      achAccountName: acct.accountHolderName || '',
      achAccountType: acct.accountType || '',
      achRoutingNumber: acct.routingNumber || '',
      achAccountNumber: acct.accountNumber || '',
      method: acct.method || '',
      extraLines: (acct.extraLines || []).map((l) => ({ label: l.label || '', value: l.value || '' })),
    }));
  }, []);

  // A CHOSEN ACCOUNT THAT THIS BRAND CANNOT USE IS DROPPED, LOUDLY-ENOUGH.
  //
  // Changing the brand after picking an account would otherwise leave the other
  // brand's name selected in the box while its digits sat in the fields below.
  // The digits are left alone deliberately — they may have been edited by hand,
  // and silently blanking what someone typed is worse than an unlinked copy.
  useEffect(() => {
    if (!bankAccountId) return;
    const picked = (bankAccounts || []).find((a) => String(a._id) === String(bankAccountId));
    if (picked && !brandAccounts.some((a) => String(a._id) === String(bankAccountId))) setBankAccountId('');
  }, [brandId, bankAccountId, bankAccounts, brandAccounts]);

  // Picking a client fills the party in, and still leaves it editable — the
  // invoice is a document, and the name on it is occasionally not the name in
  // the CRM.
  useEffect(() => {
    if (!client) return;
    if (skipClientFill.current) { skipClientFill.current = false; return; }
    setClientName(client.name || '');
    setClientCountry(client.country || '');
    if (client.currency) setCurrency(client.currency);
  }, [client]);

  const totals = useMemo(() => previewTotals(lineItems, applyTax, taxRate), [lineItems, applyTax, taxRate]);

  const setItem = (i, key, val) => setLineItems((p) => p.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const addItem = () => setLineItems((p) => [...p, { description: "", quantity: 1, unitPrice: 0 }]);
  const removeItem = (i) => setLineItems((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError("");
    if (!invoiceNumber) { setError("Invoice number is required"); return; }
    if (!brandId) { setError("Pick the brand this invoice is from"); return; }
    // Caught here as well as on the server, so the operator is told before the
    // round trip. The server refuses it too — this form is not the only way in.
    if (periodStart && periodEnd && periodEnd < periodStart) {
      setError("The invoice period ends before it starts — check the effective dates");
      return;
    }
    setSaving(true);
    try {
      // The party the invoice already carries is kept unless a client is picked
      // to replace it: an edit that blanked the email would quietly cut the
      // reminder loop's recipient, and the address is not on this form at all.
      const keep = (invoice && invoice.clientParty) || {};
      const body = {
        invoiceNumber,
        brand_id: brandId,
        currency,
        issuedAt: issuedAt || null,
        dueDate: dueDate || null,
        // The span billed for. Null is a real answer — plenty of invoices do
        // not cover a period — so it is sent rather than left out, which is how
        // clearing one on an edit takes effect.
        periodStart: periodStart || null,
        periodEnd: periodEnd || null,
        projectName,
        project_id: projectId || null,
        payAppNumber,
        retainage: retainage === "" ? 0 : Number(retainage),
        reminderLadder,
        // The writer's zone — what a trade chase's 09:00 start means when the
        // client has no timezone of its own.
        timeZone: browserTimeZone(),
        brandParty: (invoice && invoice.brandParty) || { name: (brand && brand.name) || '' },
        clientParty: {
          name: clientName,
          country: clientCountry,
          email: (client && client.email) || keep.email || '',
          address: (client && client.address) || keep.address || '',
        },
        signatureImage,
        applyTax,
        taxRate: applyTax ? taxRate : 0,
        paymentDetails: ach,
        // Provenance: which catalogue account the details above were copied
        // from. Null when they were typed by hand, which is a true statement
        // about the invoice and not a missing value.
        bankAccount_id: bankAccountId || null,
        // The mailbox reminders for this invoice send from. The server reads
        // the address off the account — a From: typed to differ from the
        // mailbox behind it only produces mail that fails its own SPF.
        account_id: accountId || null,
        // An uploaded invoice's money is the typed figure; a built one's is its
        // line items. Sending the wrong one of these zeroes the invoice.
        ...(uploaded ? { total: Number(total) || 0 } : { lineItems }),
        ...(client && client.contact_id ? { contact_id: client.contact_id } : {}),
      };

      const saved = editing
        ? await updateInvoice(invoice._id, body)
        : await createInvoice(body);
      const id = editing ? invoice._id : (saved && saved.data && saved.data._id);

      // Recompile so the document says what the invoice now says. An uploaded
      // invoice is skipped: its document is the PDF that was handed to us, and
      // compiling would write an empty line-item invoice over it.
      const gen = (id && !uploaded) ? await generateInvoice(id) : null;
      // The board updates behind the preview, so the invoice is not "missing"
      // while it is being looked at.
      onRefresh && onRefresh();

      // A LINK ISSUED FOR THE OLD AMOUNT NO LONGER FITS THIS INVOICE.
      //
      // Stripe prices are fixed at creation, so the URL already in a client's
      // inbox still charges what it charged. The next request builds a fresh
      // one (reuse compares amount AND currency), but nobody would think to ask
      // unless they were told.
      const hadLink = !!(invoice && invoice.paymentLink && invoice.paymentLink.url);
      const moneyMoved = editing && hadLink && (
        Number(invoice.total) !== Number((gen && gen.data && gen.data.total) != null ? gen.data.total : (uploaded ? Number(total) || 0 : invoice.total))
        || String(invoice.currency || '') !== String(currency)
      );

      const html = gen && gen.data && gen.data.document && gen.data.document.html;
      if (html) { setPreview({ html, number: invoiceNumber, moneyMoved }); return; }
      if (editing) { setPreview({ html: '', number: invoiceNumber, moneyMoved }); return; }
      onSaved && onSaved();
    } catch (e) {
      setError((e && (e.error || e.message)) || (editing ? "Failed to save the invoice" : "Failed to create invoice"));
    } finally {
      setSaving(false);
    }
  };

  if (preview) {
    return (
      <Modal title={`Invoice ${preview.number} — this is what the client receives`} onClose={() => onSaved && onSaved()} wide>
        <p style={{ fontSize: 13, color: '#666' }}>
          {preview.html
            ? 'Saved and recompiled. This exact document is what a reminder attaches — check the parties, the dates and the totals now rather than after it has been sent.'
            : 'Saved. This invoice keeps the PDF that was uploaded for it, so there is nothing to recompile.'}
        </p>
        {preview.moneyMoved && (
          <div className="alert alert-warning" style={{ fontSize: 13 }}>
            The amount changed and this invoice already had a Stripe payment link.
            That link is fixed at the old price and still charges it — press
            <strong> Create link</strong> under Set reminder to issue one for the new total.
          </div>
        )}
        {preview.html ? (
          <iframe
            sandbox="allow-same-origin"
            srcDoc={preview.html}
            title="Invoice preview"
            style={{ width: '100%', height: '62vh', border: '1px solid #e6e9ec', borderRadius: 4, background: '#fff' }}
          />
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          {preview.html ? (
            <button
              className="acc-btn"
              onClick={() => {
                // Its own window, so the browser's own Save-as-PDF is available
                // without this page having to render one.
                const w = window.open('', '_blank');
                if (w) { w.document.write(preview.html); w.document.close(); }
              }}
            >
              Open / print
            </button>
          ) : null}
          <button className="acc-btn acc-btn-primary" onClick={() => onSaved && onSaved()}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={editing ? `Edit invoice ${invoice.invoiceNumber || ''}`.trim() : "Create invoice for brand"} onClose={onClose} wide>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      {/* AN INVOICE THE CLIENT HAS ALREADY SEEN IS STILL EDITABLE — and saying
          so is the whole guard. Correcting a rate on a document somebody is
          holding is a legitimate thing to do and a bad thing to do by accident,
          and only the operator knows which this is. */}
      {editing && invoice.paid && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          This invoice is marked <strong>paid</strong>. Editing it changes the record of
          what was settled — and no reminder will go out about it either way.
        </div>
      )}
      {editing && !invoice.paid && invoice.status === 'sent' && (
        <div className="alert alert-info" style={{ fontSize: 13 }}>
          This invoice has already been sent. The client is holding the old
          version — tell them what changed, or the two copies will disagree.
        </div>
      )}

      <PartyPickers
        brands={brands} clients={clients}
        brandId={brandId} setBrandId={setBrandId}
        clientId={clientId} setClientId={setClientId}
      />
      <AddressPreview
        from={fromPreview(invoice && invoice.brandParty, brand && brand.name, brandAddress)}
        brandId={brandId} brandAddress={brandAddress} brandParty={invoice && invoice.brandParty}
        billedTo={billedToPreview(client, invoice && invoice.clientParty, clientName, clientCountry)}
      />
      {editing && (
        <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
          The invoice was saved with {(invoice.clientParty && invoice.clientParty.name) || 'no client name'}
          {invoice.clientParty && invoice.clientParty.email ? ` (${invoice.clientParty.email})` : ''}.
          Saving copies the picked client's address and email onto it; "No client on file" keeps what it has.
        </div>
      )}

      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <input className="form-control" placeholder="Invoice #" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
        <input className="form-control" placeholder="Client name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
        <input className="form-control" placeholder="Client country (e.g. GB)" value={clientCountry} onChange={(e) => setClientCountry(e.target.value)} />
        <CurrencyPicker value={currency} onChange={setCurrency} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 8, maxWidth: 820 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Invoice date
          <input className="form-control" type="date" aria-label="Invoice date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Due date
          <input className="form-control" type="date" aria-label="Due date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
        {/* THE PERIOD THIS INVOICE COVERS. Separate from the due date on
            purpose: an invoice raised on 3 September, due on the 17th, can bill
            the whole of August, and nothing on the record said so. Both ends
            inclusive; leaving them empty means the invoice states no period. */}
        <label style={{ fontSize: 12, color: '#666' }}>Effective from
          <input className="form-control" type="date" aria-label="Effective from" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Effective to
          <input className="form-control" type="date" aria-label="Effective to" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </label>
      </div>
      <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
        The period of work or service this invoice bills for — both days
        included. Leave both empty for a one-off that covers no span.
      </div>

      {/* CONSTRUCTION PAY-APP FACTS. Read by the Trade contractor reminder
          ladder: the project names the job in every chase, and retainage held
          back is stated separately so nobody mistakes it for the amount due.
          Optional — leave empty for an invoice that is not a pay application. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 2fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Reminder ladder
          <select className="form-control" aria-label="Reminder ladder" value={reminderLadder} onChange={(e) => setReminderLadder(e.target.value)}>
            {LADDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label.split(' — ')[0]}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Project / job
          <ProjectPicker key={projectClient ? projectClient._id : 'none'} client={projectClient}
            projectId={projectId} setProjectId={setProjectId} projectName={projectName} setProjectName={setProjectName} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Pay app #
          <input className="form-control" aria-label="Pay app number" value={payAppNumber} onChange={(e) => setPayAppNumber(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Retainage held
          <input className="form-control" type="number" min="0" step="0.01" aria-label="Retainage" value={retainage} onChange={(e) => setRetainage(e.target.value)} />
        </label>
      </div>
      <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
        {(LADDER_OPTIONS.find((o) => o.value === reminderLadder) || LADDER_OPTIONS[0]).label}.
        Project, pay app # and retainage are for construction pay applications —
        retainage is the amount held back and is not part of the total.
      </div>

      {/* AN UPLOADED INVOICE HAS NO LINE ITEMS TO ADD UP.
          The document is the PDF that was handed to us and its total is the
          figure the operator typed. Showing the line-item grid here would
          invite somebody to fill it in, and the moment they did, the recompute
          would write their arithmetic over the real total. */}
      {uploaded ? (
        <>
          <h6 style={{ marginTop: 14, fontSize: 13 }}>Amount due</h6>
          <p style={{ fontSize: 12, color: '#8a949c', marginBottom: 6 }}>
            This invoice was uploaded as a finished PDF, so its total is this
            figure rather than a sum of lines. The PDF itself is unchanged by an
            edit here — only what this system chases and reports.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'center' }}>
            <input
              className="form-control"
              aria-label="Amount due"
              type="number" min="0" step="any"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
            <span style={{ fontSize: 13, color: '#3c4a54' }}>{currency}</span>
          </div>
        </>
      ) : (
      <>
      {/* WHAT EACH BOX IS, SAID ON SCREEN.
          Three unlabelled inputs in a row leave "Qty" and "Unit price" to be
          worked out from their order, and the number in the wrong one is a
          wrong invoice — nothing downstream can tell 2 × $100 from 100 × $2.
          The labels stay put; the placeholders show the shape of the value. */}
      <h6 style={{ marginTop: 14, fontSize: 13 }}>Line items</h6>
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1.2fr 1.2fr auto', gap: 8, fontSize: 11, color: '#6b7a86', marginBottom: 4 }}>
        <span>Description</span>
        <span>Quantity</span>
        <span>Rate ({currency} per unit)</span>
        <span>Amount</span>
        <span />
      </div>
      {lineItems.map((it, i) => (
        <div className="form-row" key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1.2fr 1.2fr auto', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <input className="form-control" aria-label="Description" placeholder="What is being billed" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
          <input className="form-control" aria-label="Quantity" type="number" min="0" step="any" placeholder="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
          <input className="form-control" aria-label="Rate" type="number" min="0" step="any" placeholder="0.00" value={it.unitPrice} onChange={(e) => setItem(i, "unitPrice", e.target.value)} />
          {/* Quantity × rate, shown as it is typed — the line's own arithmetic
              is the cheapest way to catch a number in the wrong box. */}
          <span style={{ fontSize: 13, color: '#3c4a54' }}>{fmtMoney(round2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0)), currency)}</span>
          <button type="button" className="acc-btn" title="Remove this line" onClick={() => removeItem(i)}>×</button>
        </div>
      ))}
      <button type="button" className="acc-btn" onClick={addItem}>+ Add line item</button>

      <div className="form-row" style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12 }}>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={applyTax} onChange={(e) => setApplyTax(e.target.checked)} /> Apply tax
        </label>
        {applyTax && (
          <input className="form-control" style={{ width: 100 }} type="number" placeholder="Tax %" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
        )}
      </div>
      </>
      )}

      {/* WHO THE CLIENT HEARS FROM.
          Chosen here, where the brand and the client are already on screen,
          rather than discovered later on the reminder form — the loop that
          opens with this invoice copies it. Picking one also REMEMBERS it as
          the brand's invoicing address, so the next invoice for this brand
          starts here instead of asking again. */}
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginTop: 10 }}>
        Send from <span style={{ color: '#8a949c' }}>— the address reminders for this invoice come from</span>
        <select
          className="form-control"
          value={accountId}
          onChange={(e) => { setAccountId(e.target.value); setAccountTouched(true); }}
        >
          <option value="">{(accounts || []).length ? 'Pick a mailbox…' : 'No mailboxes available'}</option>
          {ownMailboxes.map((a) => (
            <option key={a._id} value={a._id}>
              {a.fromAddress || a.name}{a.status !== 'connected' ? ' (not connected)' : ''}
            </option>
          ))}
          {otherMailboxes.length > 0 && (
            <optgroup label="Other brands">
              {otherMailboxes.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.fromAddress || a.name}{a.status !== 'connected' ? ' (not connected)' : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {/* An empty picker used to say nothing at all, which is how a 403 on the
            mailbox list looked like "there are no mailboxes". */}
        {accountsErr
          ? <span style={{ fontSize: 11, color: '#a15c00' }}>{accountsErr}</span>
          : !(accounts || []).length
            ? <span style={{ fontSize: 11, color: '#a15c00' }}>Connect one under Email setup first.</span>
            : accountId && crossBrandMailbox
              ? (
                <span style={{ fontSize: 11, color: '#a15c00' }}>
                  That mailbox belongs to another brand. The client will see mail from a company
                  they have no relationship with.
                </span>
              )
              : accountId
                ? (
                  <span style={{ fontSize: 11, color: '#8a949c' }}>
                    Remembered for {(brand && brand.name) || 'this brand'} — the next invoice starts here,
                    and this invoice’s reminders send from it.
                  </span>
                )
                : <span style={{ fontSize: 11, color: '#8a949c' }}>Leave it unset and the reminder loop falls back to the brand’s configured mailbox.</span>}
      </label>

      {/* THE MARK AT THE TOP LEFT — A FALLBACK, NOT AN OVERRIDE.
          This box used to be labelled "Signature image" and printed at the FOOT
          of the invoice. What operators actually put in it was a logo, so a
          brand mark came out at the bottom of a demand for payment looking like
          a signature. It now feeds the logo slot at the top left, and only when
          the brand has no logo of its own. The stored field keeps its old name
          on the record; nothing about an older invoice is rewritten. */}
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginTop: 10 }}>
        Logo image <span style={{ color: '#8a949c' }}>— optional, only used if the brand has none</span>
        <input
          className="form-control"
          placeholder="https://… or a data-uri — leave blank to use the brand’s logo"
          value={signatureImage}
          onChange={(e) => setSignatureImage(e.target.value)}
        />
        <span style={{ fontSize: 11, color: '#8a949c' }}>
          Prints at the top left. A brand that has its own logo always prints that one.
        </span>
      </label>

      {/* ACH details. They print on the invoice AND in every reminder, from
          here — never written by the drafter, which is forbidden from inventing
          bank details. Left blank, reminders simply carry none. */}
      <h6 style={{ marginTop: 14, fontSize: 13 }}>
        Bank transfer{ach.method ? ` (${ach.method})` : ''}
        <span style={{ fontWeight: 400, color: '#8a949c', fontSize: 11 }}> — repeated in every reminder</span>
      </h6>

      {/* PICK THE ACCOUNT BY NAME INSTEAD OF RETYPING IT.
          The catalogue lives in the Bank accounts tab, per brand. Choosing one
          FILLS the boxes below and leaves them editable — this is a filler, not
          an indirection, so an invoice keeps saying what it said the day it was
          sent even if the account is corrected next month. */}
      <label style={{ fontSize: 12, color: '#666', display: 'block' }}>
        Bank account
        <select
          className="form-control"
          value={bankAccountId}
          onChange={(e) => {
            const id = e.target.value;
            setBankAccountId(id);
            applyBankAccount(brandAccounts.find((a) => String(a._id) === String(id)));
          }}
        >
          <option value="">
            {brandAccounts.length ? 'Type the details in by hand…' : 'None saved — type the details in below'}
          </option>
          {brandAccounts.map((a) => (
            // Name AND method (and the account type when it has one), because
            // the same bank holds the ACH account and the wire account.
            <option key={a._id} value={a._id}>
              {accountLabel(a)}{a.brand_id ? '' : ' · company-wide'}{a.isDefault ? ' · default' : ''}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: '#8a949c' }}>
          {!brandId
            ? 'Pick a brand above to see its accounts — a brand is never offered another brand’s.'
            : brandAccounts.length
              ? 'Fills the boxes below. Edit any of them afterwards; the invoice keeps its own copy.'
              : 'Add one under the Bank accounts tab and it will be selectable here.'}
        </span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        {[
          ['achBankName', 'Bank name', 'e.g. Chase'],
          ['achAccountName', 'Account holder name', 'the name on the account'],
          ['achRoutingNumber', 'Routing number', '9 digits'],
          ['achAccountNumber', 'Account number', ''],
        ].map(([key, label, hint]) => (
          <label key={key} style={{ fontSize: 12, color: '#666' }}>
            {label}
            <input
              className="form-control"
              placeholder={hint || label}
              value={ach[key]}
              onChange={fieldSetter(setAch, key)}
            />
          </label>
        ))}
        {/* Which KIND of account, which a bank needs and a name does not give
            them. Left blank it simply does not print. */}
        <label style={{ fontSize: 12, color: '#666' }}>
          Account type
          <select
            className="form-control"
            value={ach.achAccountType}
            onChange={fieldSetter(setAch, 'achAccountType')}
          >
            <option value="">Not stated</option>
            <option value="Checking">Checking</option>
            <option value="Savings">Savings</option>
            <option value="Business Checking">Business Checking</option>
          </select>
        </label>
      </div>

      {/* IBAN, SWIFT, a sort code — carried from the chosen account. Shown
          because they PRINT: a EUR account whose IBAN was invisible in this
          form is a form that does not show what the client will receive. Edited
          on the account itself, in the Bank accounts tab, so one correction
          fixes every future invoice rather than this one. */}
      {(ach.extraLines || []).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#666', background: '#f7f8f9', borderRadius: 4, padding: '8px 10px' }}>
          {(ach.extraLines || []).map((l, i) => (
            <div key={i}><span style={{ color: '#8a949c' }}>{l.label}:</span> {l.value}</div>
          ))}
          <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
            From the selected account. Change them under Bank accounts.
          </div>
        </div>
      )}

      <div className="invoice-totals" style={{ marginTop: 12, fontSize: 13 }}>
        {uploaded ? (
          <div><strong>Total: {fmtMoney(round2(Number(total) || 0), currency)}</strong></div>
        ) : (
          <>
            <div>Subtotal: {totals.subtotal}</div>
            {applyTax && <div>Tax: {totals.taxAmount}</div>}
            <div><strong>Total: {totals.total}</strong></div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={saving}>
          {saving
            ? "Saving…"
            : (editing ? (uploaded ? "Save changes" : "Save + recompile PDF") : "Create + Compile PDF")}
        </button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Upload invoice
// The second way in. The document already exists; what is missing is everything
// AROUND it — which brand billed, who owes, how much, by when — because that is
// what the payout figures and the reminder emails are built from.
export function UploadInvoiceModal({ brands, clients, onClose, onSaved }) {
  // The date printed on the uploaded PDF. Without it the record only knows
  // when the file was uploaded, and a reminder has no invoice date to state.
  const [issuedAt, setIssuedAt] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [brandId, setBrandId] = useState("");
  const [clientId, setClientId] = useState("");
  const [total, setTotal] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [dueDate, setDueDate] = useState("");
  // The span the uploaded document bills for. The PDF says it in words; these
  // are the same statement in fields, so the board and the reminder list can
  // show it without anybody opening the file.
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [payAppNumber, setPayAppNumber] = useState("");
  const [retainage, setRetainage] = useState("");
  const [reminderLadder, setReminderLadder] = useState("standard");
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const brand = (brands || []).find((b) => String(b._id || b.id) === String(brandId));
  const client = (clients || []).find((c) => String(c._id) === String(clientId));

  const onPickPdf = async (e) => {
    setError("");
    const file = e.target.files && e.target.files[0];
    if (!file) { setPdf(null); return; }
    try {
      const dataUri = await readFileAsDataUri(file);
      setPdf(attachmentFromDataUri(dataUri, file.name));
    } catch (err) {
      setPdf(null);
      setError((err && err.message) || "Could not read PDF");
    }
  };

  const submit = async () => {
    setError("");
    if (!invoiceNumber) { setError("Invoice number is required"); return; }
    if (!brandId) { setError("Pick the brand this invoice is from"); return; }
    if (!pdf) { setError("Choose the invoice PDF"); return; }
    if (periodStart && periodEnd && periodEnd < periodStart) {
      setError("The invoice period ends before it starts — check the effective dates");
      return;
    }
    setBusy(true);
    try {
      await uploadInvoice({
        invoiceNumber,
        brand_id: brandId,
        client_id: clientId || null,
        total: Number(total) || 0,
        currency,
        issuedAt: issuedAt || null,
        dueDate: dueDate || null,
        periodStart: periodStart || null,
        periodEnd: periodEnd || null,
        projectName,
        project_id: projectId || null,
        payAppNumber,
        retainage: retainage === "" ? 0 : Number(retainage),
        reminderLadder,
        timeZone: browserTimeZone(),
        brandParty: { name: (brand && brand.name) || '' },
        clientParty: client ? undefined : {},
        pdf,
      });
      onSaved && onSaved();
    } catch (e) { setError((e && e.message) || "Failed to upload the invoice"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Upload an invoice" onClose={onClose} wide>
      <p style={{ fontSize: 13, color: '#666' }}>
        For an invoice you already have as a PDF. It lands as a normal invoice:
        it shows on the board, counts toward that brand's payout, and a reminder
        loop can be started on it — the loop attaches this exact file.
      </p>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      <PartyPickers
        brands={brands} clients={clients}
        brandId={brandId} setBrandId={setBrandId}
        clientId={clientId} setClientId={setClientId}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Invoice # *
          <input className="form-control" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="INV-1042" />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Amount due
          <input className="form-control" type="number" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="1200" />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Currency
          <CurrencyPicker value={currency} onChange={setCurrency} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Invoice date
          <input className="form-control" type="date" aria-label="Invoice date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Due date
          <input className="form-control" type="date" aria-label="Due date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
      </div>

      {/* The period the uploaded invoice covers — the same pair of dates a
          built invoice carries, because an uploaded one is not a lesser
          invoice and the same lists have to answer "which month is this for?" */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Effective from
          <input className="form-control" type="date" aria-label="Effective from" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Effective to
          <input className="form-control" type="date" aria-label="Effective to" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </label>
        <div style={{ fontSize: 11, color: '#8a949c', alignSelf: 'end', paddingBottom: 6 }}>
          The period this invoice bills for, both days included. Optional.
        </div>
      </div>

      {/* CONSTRUCTION PAY-APP FACTS. Read by the Trade contractor reminder
          ladder: the project names the job in every chase, and retainage held
          back is stated separately so nobody mistakes it for the amount due.
          Optional — leave empty for an invoice that is not a pay application. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 2fr 1fr 1fr', gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#666' }}>Reminder ladder
          <select className="form-control" aria-label="Reminder ladder" value={reminderLadder} onChange={(e) => setReminderLadder(e.target.value)}>
            {LADDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label.split(' — ')[0]}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Project / job
          <ProjectPicker key={client ? client._id : 'none'} client={client}
            projectId={projectId} setProjectId={setProjectId} projectName={projectName} setProjectName={setProjectName} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Pay app #
          <input className="form-control" aria-label="Pay app number" value={payAppNumber} onChange={(e) => setPayAppNumber(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: '#666' }}>Retainage held
          <input className="form-control" type="number" min="0" step="0.01" aria-label="Retainage" value={retainage} onChange={(e) => setRetainage(e.target.value)} />
        </label>
      </div>
      <div style={{ fontSize: 11, color: '#8a949c', marginTop: 4 }}>
        {(LADDER_OPTIONS.find((o) => o.value === reminderLadder) || LADDER_OPTIONS[0]).label}.
        Project, pay app # and retainage are for construction pay applications —
        retainage is the amount held back and is not part of the total.
      </div>

      <label style={{ fontSize: 12, color: '#666', display: 'block', marginTop: 12 }}>Invoice PDF *
        <input className="form-control" type="file" accept="application/pdf,.pdf" onChange={onPickPdf} />
        <div style={{ fontSize: 11, color: '#888' }}>
          {pdf ? <>Ready: <strong>{pdf.filename}</strong></> : 'The file every reminder about this invoice will carry.'}
        </div>
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? "Uploading…" : "Upload invoice"}</button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Set reminder
// The escalation ladders (prigmar-worker services/invoiceLadders.js). The
// labels ARE the rule, so nobody has to remember what a name meant; `schedule`
// is the same table in days, shown under the picker.
export const LADDER_OPTIONS = [
  {
    value: 'standard',
    label: 'Standard — warm notices 1-3, serious from the 10th, debt collection from the 15th',
    schedule: 'Gaps of 10, 10, then 7 days (day 0, 10, 20, 27 … serious at day 69, collections at day 104, then every 14 days). Payment link from the 8th email.',
  },
  {
    value: 'trade',
    label: 'Trade contractor — payment request from day 35, debt collection from day 85',
    schedule: 'Counted from the due date: warm notices on days 0, 7, 14; follow-ups on 21 and 28 with no payment request; the request from day 35 (then every 5 days); serious from day 50; debt collection from day 85, then every 7 days. No lien is ever mentioned. The Stripe card link is added at the end of every email from day 50. Left empty, the start is 9:00 AM on the due date in the client\'s timezone.',
  },
  {
    value: 'original',
    label: 'Original — payment link from the 3rd email, debt collection from the 5th',
    schedule: 'Weekly, then every 3 days.',
  },
];

export function SetReminderModal({ invoices, accounts, accountsErr, brandName, preselect, onClose, onSaved }) {
  const [invoiceId, setInvoiceId] = useState(preselect || "");
  const [accountId, setAccountId] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [toAddresses, setToAddresses] = useState("");
  const [ccAddresses, setCcAddresses] = useState("");
  const [payLink, setPayLink] = useState("");
  // Why there is no link, in the words the server used. Its own state rather
  // than the modal's `error`, because it is not a reason to stop setting up the
  // reminder — the loop is worth starting whether or not a link exists.
  const [linkNote, setLinkNote] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attachment, setAttachment] = useState(null);
  // WHEN THE CHASE BEGINS. Empty means the default and only previous
  // behaviour: it starts the moment Sending is switched on. A date here moves
  // that one act — the cadence after the first email is unchanged.
  const [startAt, setStartAt] = useState("");
  // THE ZONE THAT WALL CLOCK IS IN (2026-10-15) — usually the client's. The
  // browser's own by default; a loop opens in the zone it was saved with.
  const [startZone, setStartZone] = useState(browserTimeZone());
  const startIso = startAt ? zonedInputToIso(startAt, startZone) : '';
  // WHICH LADDER THE CHASE CLIMBS. 'standard' is every new loop; 'original'
  // keeps the rungs from before 2026-09-15 (see LADDER_OPTIONS).
  const [ladder, setLadder] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const invoice = (invoices || []).find((i) => String(i._id) === String(invoiceId));
  // A start already behind us is not a start; the hint says so rather than
  // showing a date the loop will never wait for.
  const startPassed = !!startIso && !startIsPending(startIso);

  // ONE LOOP PER INVOICE — SO SAY SO WHEN ONE ALREADY EXISTS.
  //
  // The server fills in the invoice's existing chase rather than adding a
  // second (two loops for one debt means the client is chased twice, a minute
  // apart). When the existing one had been ARCHIVED that was invisible: the
  // form looked like it created nothing, because the row it wrote to was in the
  // Archive tab. Saying which of the two is about to happen is the difference
  // between "nothing happened" and "this is the one you already have".
  // Fetched here rather than threaded down: this modal opens from the Invoices
  // tab too, where the page has no loops loaded at all. `archived: 'all'`
  // because the archived ones are exactly the case worth warning about.
  const [loops, setLoops] = useState([]);
  useEffect(() => {
    let live = true;
    listReminders({ archived: 'all' })
      .then((r) => { if (live) setLoops((r && r.data) || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  const existingLoop = loops.find((r) => String(r.invoice_id) === String(invoiceId));

  // Saving RECONFIGURES the invoice's existing chase rather than adding one, so
  // the form has to open showing what that chase is already set to. A start
  // date that silently reset to "immediately" every time the form was opened
  // would be a scheduled chase quietly rearmed to go now.
  useEffect(() => {
    if (existingLoop) {
      const zone = existingLoop.scheduleTimeZone || browserTimeZone();
      setStartZone(zone);
      setStartAt(toZonedInputValue(existingLoop.scheduledStartAt, zone));
    }
  }, [existingLoop && existingLoop._id, existingLoop && existingLoop.scheduledStartAt]); // eslint-disable-line react-hooks/exhaustive-deps
  // Same reason: a loop kept on the original ladder must open showing it, or a
  // re-save would move it onto the standard rungs without anyone choosing that.
  // With no loop yet, the ladder picked on the INVOICE is the answer.
  useEffect(() => {
    setLadder((existingLoop && existingLoop.ladder) || (invoice && invoice.reminderLadder) || "standard");
  }, [existingLoop && existingLoop._id, existingLoop && existingLoop.ladder, invoice && invoice._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // THE MAILBOXES THAT BELONG TO THIS INVOICE'S BRAND.
  //
  // An invoice from BidLight chased from support@prigmar.com is a mismatch the
  // client sees before we do: the mail arrives from a company they have no
  // relationship with, about money they owe someone else. So the brand's own
  // mailboxes come first, and everything else is behind a divider — available,
  // because a shared mailbox for a small brand is a real arrangement, but never
  // the accidental pick.
  const invoiceBrand = invoice && invoice.brand_id ? String(invoice.brand_id) : '';
  const [ownMailboxes, otherMailboxes] = useMemo(() => {
    const all = accounts || [];
    if (!invoiceBrand) return [all, []];
    return [
      all.filter((a) => String(a.brand_id || '') === invoiceBrand),
      all.filter((a) => String(a.brand_id || '') !== invoiceBrand),
    ];
  }, [accounts, invoiceBrand]);

  // Changing the invoice to another brand must not leave the previous brand's
  // mailbox silently selected underneath.
  useEffect(() => {
    if (!accountId || !invoiceBrand) return;
    const picked = (accounts || []).find((a) => String(a._id) === String(accountId));
    if (picked && String(picked.brand_id || '') !== invoiceBrand) setAccountId('');
  }, [invoiceBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  const crossBrand = !!accountId && !!invoiceBrand
    && !ownMailboxes.some((a) => String(a._id) === String(accountId));

  // THE PAYMENT LINK, VISIBLE BEFORE ANY CLIENT SEES IT.
  //
  // Read-only on purpose: it is issued by Stripe for this exact amount, and a
  // hand-edited one would point somewhere we cannot vouch for. It goes out with
  // the EIGHTH reminder whatever is shown here — this is for checking it.
  useEffect(() => {
    setCopied(false);
    setLinkNote('');
    setPayLink((invoice && invoice.paymentLink && invoice.paymentLink.url) || '');
    if (!invoiceId) return;
    // Ask up front whether a link CAN be made. A brand with no Stripe key, a
    // zero total or a currency Stripe will not take are all things the operator
    // has to go and fix — saying so here beats letting them press a button and
    // watch nothing happen, which is what this surface used to do.
    let live = true;
    (async () => {
      try {
        const r = await getPaymentLinkStatus(invoiceId);
        const d = (r && r.data) || {};
        if (!live) return;
        if (d.url) setPayLink(d.url);
        else if (d.staleUrl) setLinkNote('The existing link is for a different amount or currency — make a new one.');
        else if (!d.ready) setLinkNote(d.message || '');
      } catch (_) { /* the button still reports for itself */ }
    })();
    return () => { live = false; };
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ONE REQUEST, ONE ANSWER.
  //
  // This used to fire an outbox event, get back "queued", and poll the invoice
  // eight times hoping a URL appeared. When the brand had no Stripe key the
  // worker skipped the job in silence, the polling found nothing, and the
  // button went back to reading "Create link" with no message anywhere. The
  // server now builds the link in the request and returns either the URL or the
  // reason there is none.
  const makeLink = async () => {
    if (!invoiceId) return;
    setLinkBusy(true); setLinkNote('');
    try {
      const r = await createPaymentLink(invoiceId);
      const url = r && r.data && r.data.url;
      if (url) { setPayLink(url); return; }
      setLinkNote((r && r.error) || 'Stripe returned no link.');
    } catch (e) {
      setLinkNote((e && (e.error || e.message)) || 'Could not create the payment link');
    } finally { setLinkBusy(false); }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(payLink);
      else {
        // Older browsers, and any page not served over https.
        const el = document.createElement('textarea');
        el.value = payLink; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* the field is selectable either way */ }
  };

  // The client on the invoice is who owes the money — offer it rather than
  // making the operator retype an address the invoice already carries.
  useEffect(() => {
    if (invoice && invoice.clientParty && invoice.clientParty.email && !toAddresses) {
      setToAddresses(invoice.clientParty.email);
    }
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // The mailbox picked supplies the From: address; typing a different one would
  // only produce mail that fails its own SPF.
  useEffect(() => {
    const acc = (accounts || []).find((a) => String(a._id) === String(accountId));
    if (acc) setFromAddress(acc.fromAddress || '');
  }, [accountId, accounts]);

  const onPickPdf = async (e) => {
    setError("");
    const file = e.target.files && e.target.files[0];
    if (!file) { setAttachment(null); return; }
    try {
      const dataUri = await readFileAsDataUri(file);
      setAttachment(attachmentFromDataUri(dataUri, file.name));
    } catch (err) {
      setAttachment(null);
      setError((err && err.message) || "Could not read PDF");
    }
  };

  const start = async () => {
    setError("");
    if (!invoiceId) { setError("Pick an invoice"); return; }
    const cc = ccAddresses.split(",").map((x) => x.trim()).filter(Boolean);
    setBusy(true);
    try {
      await createReminder({
        invoice_id: invoiceId,
        account_id: accountId || null,
        fromAddress,
        toAddresses: toAddresses.split(",").map((s) => s.trim()).filter(Boolean),
        // Only sent when the operator typed something. An empty array would
        // OVERRIDE the inheritance with "nobody" — leaving the key out is what
        // makes the server fall back to the client record's CC list, which is
        // where that list is actually maintained.
        ...(cc.length ? { ccAddresses: cc } : {}),
        // Always sent, including as null: clearing a start that was set
        // earlier has to be as possible as setting one, and on this form an
        // empty box IS the instruction "start when I switch it on".
        scheduledStartAt: startIso || null,
        scheduleTimeZone: startZone,
        ladder,
        attachmentPdf: attachment || undefined,
      });
      onSaved && onSaved();
    } catch (e) { setError((e && e.message) || "Failed to start reminder"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Set invoice reminder" onClose={onClose} wide>
      <p style={{ fontSize: 13, color: '#666' }}>
        A reminder loop emails the client from a chosen mailbox on the schedule
        of its escalation ladder (below), drafting each one with AI and cleaning
        it with the perfectionist, until the invoice is marked paid.
      </p>
      <div className="alert alert-info" style={{ fontSize: 13 }}>
        It is created <strong>switched off</strong> and sends nothing. Turn on
        “Sending” in the Reminders list when you want the first email to go —
        or set a start date below and switching it on will hold it until then.
      </div>
      {existingLoop && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          This invoice already has a chase{existingLoop.archived ? ' in the Archive tab' : ''}
          {Number(existingLoop.remindersSent) > 0
            ? ` — ${existingLoop.remindersSent} reminder${Number(existingLoop.remindersSent) === 1 ? '' : 's'} already sent`
            : ''}.
          Saving <strong>reconfigures that one</strong> rather than adding a second — one
          debt, one chase.{existingLoop.archived ? ' It comes back onto the Reminders tab.' : ''}
        </div>
      )}
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}
      <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Invoice *
        <select className="form-control" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
          <option value="">Select invoice…</option>
          {invoices.map((inv) => (
            <option key={inv._id} value={inv._id}>
              {inv.invoiceNumber} — {inv.currency} {inv.total}{inv.source === 'uploaded' ? ' (uploaded)' : ''}
            </option>
          ))}
        </select>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 12 }}>Send from *
          <select className="form-control" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">{(accounts || []).length ? 'Pick a mailbox…' : 'No mailboxes available'}</option>
            {ownMailboxes.map((a) => (
              <option key={a._id} value={a._id}>{a.fromAddress || a.name}{a.status !== 'connected' ? ' (not connected)' : ''}</option>
            ))}
            {otherMailboxes.length > 0 && (
              <optgroup label="Other brands">
                {otherMailboxes.map((a) => (
                  <option key={a._id} value={a._id}>{a.fromAddress || a.name}{a.status !== 'connected' ? ' (not connected)' : ''}</option>
                ))}
              </optgroup>
            )}
          </select>
          {/* An empty picker used to say nothing at all, which is how a 403 on
              the mailbox list looked like "there are no mailboxes". */}
          {accountsErr
            ? <span style={{ fontSize: 11, color: '#a15c00' }}>{accountsErr}</span>
            : !(accounts || []).length
              ? <span style={{ fontSize: 11, color: '#a15c00' }}>Connect one under Email setup first.</span>
              : crossBrand
                ? (
                  <span style={{ fontSize: 11, color: '#a15c00' }}>
                    That mailbox belongs to another brand. The client will see mail from a company
                    they have no relationship with.
                  </span>
                )
                : (invoiceBrand && !ownMailboxes.length
                  ? <span style={{ fontSize: 11, color: '#a15c00' }}>
                    No mailbox for {brandName ? brandName(invoiceBrand) || 'this brand' : 'this brand'} — pick one under “Other brands” or connect one.
                  </span>
                  : null)}
        </label>
        <label style={{ fontSize: 12 }}>To address(es)
          <input className="form-control" placeholder="comma-separated" value={toAddresses} onChange={(e) => setToAddresses(e.target.value)} />
        </label>
      </div>

      {/* WHEN THE CHASE BEGINS.
          Empty is the default and the behaviour this package was built on:
          turning Sending on sends. A date here moves that one act and nothing
          else — the ladder's schedule still runs from the first email onward. The invoice's own due date is shown beside it
          because "start chasing the day after it falls due" is the decision
          being made, and it cannot be made without that date in view. */}
      <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Start sending
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input
            type="datetime-local"
            className="form-control"
            aria-label="Start sending"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
          {/* The time above is read in THIS zone — pick the client's to have
              the first reminder land at 09:00 on their clock. */}
          <select className="form-control" aria-label="Start timezone" value={startZone} onChange={(e) => setStartZone(e.target.value)}>
            {timeZoneOptions(startZone, browserTimeZone()).map((z) => (
              <option key={z} value={z}>{z.replace(/_/g, ' ')}{z === browserTimeZone() ? ' (yours)' : ''}</option>
            ))}
          </select>
        </div>
        <span style={{ fontSize: 11, color: startPassed ? '#a15c00' : '#8a949c' }}>
          {!startAt
            ? <>Leave empty to begin the moment you switch Sending on. {invoice && invoice.dueDate ? <>This invoice is due {fmtDate(invoice.dueDate)}.</> : null}</>
            : startPassed
              ? 'That time has passed — this loop will begin as soon as you switch Sending on.'
              : <>Switching Sending on will <strong>arm</strong> this loop; the first reminder goes out {fmtInZone(startIso, startZone)}
                {startZone !== browserTimeZone() ? <> ({fmtInZone(startIso, browserTimeZone())} your time)</> : null}, then on the ladder's schedule below.</>}
        </span>
      </label>

      {/* WHEN THE LINK AND THE COLLECTIONS SENTENCE JOIN THE CHASE.
          Enforced by the worker on every draft and again at send time; this
          only picks which rungs apply to this loop. */}
      <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Escalation ladder
        <select className="form-control" value={ladder} onChange={(e) => setLadder(e.target.value)}>
          {LADDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={{ fontSize: 11, color: '#8a949c' }}>
          {(LADDER_OPTIONS.find((o) => o.value === ladder) || LADDER_OPTIONS[0]).schedule}
        </span>
      </label>

      {/* Everyone else who should see the chase. Left blank it inherits the
          client record's CC list, which is where "who else is on the invoices"
          is actually maintained — a second list here that quietly diverges
          from that one would be worse than having none. */}
      {/* Read-only: Stripe issues it for this exact amount, and a hand-edited
          one would point somewhere we cannot vouch for. Included from the EIGHTH
          reminder regardless — shown here so it can be checked first. */}
      <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Stripe payment link
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="form-control"
            readOnly
            value={payLink}
            placeholder={invoiceId ? 'Not created yet' : 'Pick an invoice first'}
            onFocus={(e) => e.target.select()}
            style={{ background: '#f7f8f9', fontFamily: 'monospace', fontSize: 11 }}
          />
          {payLink ? (
            <>
              <button type="button" className="acc-btn" style={{ whiteSpace: 'nowrap' }} onClick={copyLink}>
                {copied ? 'Copied' : 'Copy'}
              </button>
              <a className="acc-btn" href={payLink} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: 'nowrap', lineHeight: '32px' }}>Open ↗</a>
            </>
          ) : (
            <button type="button" className="acc-btn" disabled={!invoiceId || linkBusy} onClick={makeLink} style={{ whiteSpace: 'nowrap' }}>
              {linkBusy ? 'Creating…' : 'Create link'}
            </button>
          )}
        </div>
        {linkNote
          ? (
            <span style={{ fontSize: 11, color: '#a15c00', display: 'block' }}>
              {linkNote}
            </span>
          )
          : (
            <span style={{ fontSize: 11, color: '#8a949c' }}>
              Sent with the 3rd reminder onward. Opens a Stripe page for
              {invoice ? ` ${invoice.currency || 'USD'} ${invoice.total}` : ' the invoice total'}.
            </span>
          )}
      </label>

      <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>CC address(es)
        <input
          className="form-control"
          placeholder="comma-separated — leave blank to use the client's CC list"
          value={ccAddresses}
          onChange={(e) => setCcAddresses(e.target.value)}
        />
        <span style={{ fontSize: 11, color: '#8a949c' }}>
          Every reminder in this loop is copied to these addresses.
        </span>
      </label>

      <label style={{ fontSize: 12, display: 'block', marginTop: 12 }}>Invoice PDF (optional)
        <input className="form-control" type="file" accept="application/pdf,.pdf" onChange={onPickPdf} />
        <div style={{ fontSize: 11, color: '#888' }}>
          {attachment
            ? <>Attached <strong>{attachment.filename}</strong> — every reminder email carries this PDF.</>
            : 'Leave empty to attach the invoice’s own document — the uploaded file for an uploaded invoice, the compiled one otherwise.'}
        </div>
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={start} disabled={busy}>{busy ? "Creating…" : "Create loop (off)"}</button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Invoice card
function InvoiceCard({ inv, brandName, canConfirm, canRemind, canEdit, onPaid, onRemind, onEdit, onView, onDragStart }) {
  const col = invoiceColumn(inv);
  return (
    <div
      draggable={!!canConfirm}
      onDragStart={(e) => onDragStart(e, inv)}
      style={{
        background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
        padding: 10, marginBottom: 8, cursor: canConfirm ? 'grab' : 'default',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#15172b' }}>
            {inv.invoiceNumber || '(no number)'}
            {inv.source === 'uploaded' && <span title="uploaded PDF" style={{ marginLeft: 6, fontSize: 10, color: '#6b7a86' }}>📎 uploaded</span>}
          </div>
          <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {(inv.clientParty && inv.clientParty.name) || 'no client'}{brandName ? ` · ${brandName}` : ''}
          </div>
          {/* The job it bills against — a client with several on the go has
              several invoices that otherwise look alike. */}
          {String(inv.projectName || '').trim() && (
            <div title="Project / job" style={{ fontSize: 11, color: '#4a5560', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {inv.projectName}{inv.payAppNumber ? ` · pay app ${inv.payAppNumber}` : ''}
            </div>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoney(inv.total, inv.currency)}</div>
      </div>
      <div style={{ fontSize: 10, color: '#888', marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span>due {fmtDate(inv.dueDate)}</span>
        {inv.usedForReminding && <span>in a loop</span>}
      </div>
      {/* WHAT SPAN THIS ONE IS FOR. Only when it says: a card that printed
          "covers —" on every one-off invoice would cost a line to say nothing. */}
      {(inv.periodStart || inv.periodEnd) && (
        <div style={{ fontSize: 10, color: '#6b7a86', marginTop: 2 }} title="The period this invoice covers">
          covers {fmtPeriod(inv)}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <label style={{ fontSize: 11, color: col === 'paid' ? '#1e6b3e' : '#525252', margin: 0 }}>
          <input
            type="checkbox"
            checked={col === 'paid'}
            disabled={!canConfirm}
            onChange={(e) => onPaid(inv, e.target.checked)}
          />{' '}Paid
        </label>
        <span style={{ flex: 1 }} />
        {/* Looking at the document is not editing it, so View is not gated on
            INVOICE_EDIT — anyone who can see the card can see what it bills. */}
        {onView && (
          <button className="acc-btn" style={{ fontSize: 10, padding: '1px 8px' }} title="Open the invoice document" onClick={() => onView(inv)}>View</button>
        )}
        {canEdit && onEdit && (
          <button className="acc-btn" style={{ fontSize: 10, padding: '1px 8px' }} title="Correct this invoice" onClick={() => onEdit(inv)}>Edit</button>
        )}
        {canRemind && col !== 'paid' && (
          <button className="acc-btn" style={{ fontSize: 10, padding: '1px 8px' }} onClick={() => onRemind(inv)}>Remind</button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Invoice board
function InvoiceBoard({ rows, brandName, canConfirm, canRemind, canEdit, onPaid, onRemind, onEdit, onView }) {
  const [dragged, setDragged] = useState(null);
  const [over, setOver] = useState(null);

  const columns = useMemo(() => {
    const g = Object.fromEntries(INVOICE_COLUMNS.map((c) => [c.key, []]));
    for (const r of rows) {
      const k = invoiceColumn(r);
      (g[k] = g[k] || []).push(r);
    }
    return g;
  }, [rows]);

  // Only the paid column is a destination. Draft → Ready → Chasing is what the
  // system did to the invoice, not something to be asserted by dropping a card,
  // so a drop anywhere else either un-ticks paid or does nothing.
  const drop = (key) => {
    const inv = dragged;
    setOver(null); setDragged(null);
    if (!inv || !canConfirm) return;
    const from = invoiceColumn(inv);
    if (key === 'paid' && from !== 'paid') onPaid(inv, true);
    else if (key !== 'paid' && from === 'paid') onPaid(inv, false);
  };

  return (
    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12 }}>
      {INVOICE_COLUMNS.map((c) => {
        const tone = COLUMN_TONE[c.key];
        const items = columns[c.key] || [];
        const sum = round2(items.reduce((s, i) => s + (Number(i.total) || 0), 0));
        return (
          <div
            key={c.key}
            onDragOver={(e) => { e.preventDefault(); if (over !== c.key) setOver(c.key); }}
            onDragLeave={() => setOver((s) => (s === c.key ? null : s))}
            onDrop={() => drop(c.key)}
            style={{
              flex: '0 0 250px',
              background: over === c.key ? '#eef2ff' : '#f7f7f8',
              border: over === c.key ? '2px dashed #15172b' : '1px solid #e5e5e5',
              borderRadius: 8, padding: 10, maxHeight: '68vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ background: tone.bg, color: tone.fg, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{c.label}</span>
              <span style={{ fontSize: 11, color: '#888' }}>{items.length}</span>
            </div>
            <div style={{ fontSize: 10, color: '#9aa4ad', marginBottom: 8 }}>{c.hint}{sum ? ` · ${sum}` : ''}</div>
            {items.length === 0 && <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center', padding: 14 }}>—</div>}
            {items.map((inv) => (
              <InvoiceCard
                key={inv._id}
                inv={inv}
                brandName={brandName(inv.brand_id)}
                canConfirm={canConfirm}
                canRemind={canRemind}
                canEdit={canEdit}
                onPaid={onPaid}
                onRemind={onRemind}
                onEdit={onEdit}
                onView={onView}
                onDragStart={(e, row) => setDragged(row)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Invoice list
export function InvoiceList({ rows, brandName, canConfirm, canRemind, canEdit, onPaid, onRemind, onEdit, onView, archived = false, onArchive, busyId }) {
  return (
    <table className="cm-table">
      <thead>
        <tr>
          <th>Paid</th><th>Invoice #</th><th>Brand</th><th>Client</th><th>Project / job</th><th>Total</th>
          <th>Source</th><th>Stage</th><th>Due</th><th>Period covered</th><th>Pay link</th><th>In loop</th><th />
        </tr>
      </thead>
      <tbody>
        {rows.map((inv) => {
          const col = invoiceColumn(inv);
          const tone = COLUMN_TONE[col] || COLUMN_TONE.draft;
          return (
            <tr key={inv._id}>
              <td>
                <input
                  type="checkbox"
                  checked={col === 'paid'}
                  disabled={!canConfirm}
                  onChange={(e) => onPaid(inv, e.target.checked)}
                />
              </td>
              <td>{inv.invoiceNumber || '—'}</td>
              <td>{brandName(inv.brand_id) || '—'}</td>
              <td>{(inv.clientParty && inv.clientParty.name) || '—'}</td>
              <td>{String(inv.projectName || '').trim() || '—'}</td>
              <td>{fmtMoney(inv.total, inv.currency)}</td>
              <td>{inv.source === 'uploaded' ? '📎 uploaded' : 'built'}</td>
              <td><span style={{ background: tone.bg, color: tone.fg, padding: '1px 8px', borderRadius: 10, fontSize: 11 }}>{col}</span></td>
              <td>{fmtDate(inv.dueDate)}</td>
              {/* THE DATES THE INVOICE IS EFFECTIVE BETWEEN — the span of work
                  it bills for, which is neither when it was sent nor when it
                  falls due. A retainer raised on 3 September for August reads
                  as a September invoice from every other column here. */}
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} title="The period this invoice covers — both days included">
                {fmtPeriod(inv)}
              </td>
              {/* The Stripe page for exactly this amount. Shown so it can be
                  checked before a client ever sees it — a link that goes to the
                  wrong sum is worse than no link, and there is no other way to
                  look at it. Created with the eighth reminder. */}
              <td style={{ fontSize: 11 }}>
                {inv.paymentLink && inv.paymentLink.url ? (
                  <a href={inv.paymentLink.url} target="_blank" rel="noopener noreferrer" title={inv.paymentLink.url}>
                    open ↗
                    {Number(inv.paymentLink.amount) !== Number(inv.total)
                      // The link is regenerated when the amount changes; if this
                      // ever shows, it is stale and says so rather than lying.
                      ? <span style={{ color: '#a15c00' }}> (amount changed)</span>
                      : null}
                  </a>
                ) : <span style={{ color: '#a0a8ae' }}>—</span>}
              </td>
              <td>{inv.usedForReminding ? '✓' : '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {/* THE DOCUMENT, WHICHEVER WAY IT GOT HERE. An uploaded
                    invoice opens its PDF; a built one opens the artifact
                    compiled from its line items. Available on archived rows
                    too — an invoice put away is still a record of what was
                    billed, and reading it changes nothing. */}
                {onView && (
                  <button
                    className="acc-btn"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    title={inv.source === 'uploaded' ? 'Open the uploaded invoice PDF' : 'Open the compiled invoice'}
                    onClick={() => onView(inv)}
                  >
                    View
                  </button>
                )}
                {/* Correcting an invoice beats deleting and rewriting one: the
                    number, the reminder loop and the record of what was billed
                    all survive the edit. */}
                {canEdit && onEdit && !archived && (
                  <button
                    className="acc-btn ms-1"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    title="Correct this invoice — it keeps its number and its reminder loop"
                    onClick={() => onEdit(inv)}
                  >
                    Edit
                  </button>
                )}
                {canRemind && col !== 'paid' && !archived && (
                  <button className="acc-btn ms-1" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onRemind(inv)}>Remind</button>
                )}
                {onArchive && (
                  <button
                    className="acc-btn ms-1"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    disabled={busyId === inv._id}
                    title={archived ? 'Put it back on the board' : 'Take it off the board — nothing is deleted'}
                    onClick={() => onArchive(inv, !archived)}
                  >
                    {archived ? 'Restore' : 'Archive'}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ────────────────────────────────────────────────────────────── Reminder rows
// The loops. The toggle is the whole point of this table: everything else is
// there to tell the operator what turning it on would send, and to whom.
export function ReminderList({ rows, invoiceOf, brandName, canConfirm, busyId, onToggle, onPreview, onConfirm, archived = false, onArchive }) {
  return (
    <table className="cm-table">
      <thead>
        <tr>
          <th>{archived ? 'Archived' : 'Sending'}</th><th>Invoice</th><th>Period covered</th><th>Status</th><th>Brand</th><th>To</th>
          <th>Started</th><th>Sent</th><th>Next</th><th>Reply</th><th>Recovered</th><th />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const inv = invoiceOf(r.invoice_id);
          const on = !!r.sendingEnabled;
          const settled = !!r.paymentConfirmed;
          // One function decides this for the pill, the tooltip and the confirm
          // dialog, so the three cannot describe the same loop differently.
          const state = reminderSendState(r);
          const armed = state === 'armed';     // on, but holding for its start
          const waiting = state === 'waiting';  // off, with a start still ahead
          return (
            <tr key={r._id}>
              <td>
                {archived ? (
                  // An archived loop sends nothing, so an ON/OFF control here
                  // would be a switch that does not switch anything. What it
                  // was when it was put away is the useful thing to show.
                  <span style={{ fontSize: 11, color: '#8a949c' }} title={r.archivedAt ? `Archived ${fmtCell(r.archivedAt)}` : 'Archived'}>
                    {fmtCell(r.archivedAt) || 'archived'}{r.sendingEnabled ? ' (was on)' : ''}
                  </span>
                ) : (
                <button
                  className="acc-btn"
                  disabled={!canConfirm || settled || busyId === r._id}
                  onClick={() => onToggle(r, !on)}
                  title={settled
                    ? 'Settled — this loop is finished'
                    : on
                      ? (armed ? `Armed — the first reminder goes ${fmtInZone(r.nextReminderAt, r.scheduleTimeZone)}. Press to stop.` : 'Stop sending')
                      : (waiting ? `Start this loop — it is set to begin ${fmtInZone(r.scheduledStartAt, r.scheduleTimeZone)}, not immediately` : 'Start sending — the first email goes on the next tick')}
                  style={{
                    fontSize: 11, padding: '2px 10px', borderRadius: 12,
                    background: settled ? '#f1f2f4' : (armed ? '#fff6e5' : (on ? '#e7f5ec' : '#fff')),
                    color: settled ? '#8a949c' : (armed ? '#8a5a00' : (on ? '#1e6b3e' : '#5b6670')),
                    border: `1px solid ${settled ? '#d8dce0' : (armed ? '#8a5a00' : (on ? '#1e6b3e' : '#d8dce0'))}`,
                  }}
                >
                  {/* ON and ARMED are both "sending is enabled" and mean
                      opposite things on the day: one has sent, the other is
                      waiting for a date. A single green ● for both is how an
                      operator concludes a scheduled chase is broken. */}
                  {settled ? 'settled' : (armed ? '◷ ARMED' : (on ? '● ON' : '○ OFF'))}
                </button>
                )}
              </td>
              <td>
                {inv ? inv.invoiceNumber : String(r.invoice_id || '—')}
                {inv && inv.source === 'uploaded' && <span style={{ fontSize: 10, color: '#6b7a86' }}> 📎</span>}
                {inv ? <div style={{ fontSize: 11, color: '#888' }}>{fmtMoney(inv.total, inv.currency)}</div> : null}
              </td>
              {/* WHAT THE CHASED INVOICE COVERS. Read off the invoice rather
                  than copied onto the loop: the loop chases a debt, the invoice
                  is what states the period, and a second copy here is how the
                  two would come to disagree after an edit. A loop whose invoice
                  record is gone can only say nothing. */}
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} title={inv ? 'The period the chased invoice covers' : 'The invoice record is gone'}>
                {inv ? fmtPeriod(inv) : <span style={{ color: '#a0a8ae' }}>—</span>}
              </td>
              {/* PAID / UNPAID, straight off the invoice.
                  Derived with invoiceColumn — the same function the Invoices
                  tab renders its Stage pill from — so the two boards can never
                  disagree about whether the money arrived. A second, local
                  reading of inv.paid/inv.status here is exactly how they would
                  drift.
                  paymentConfirmed also counts as paid: confirming payment on a
                  loop is the operator asserting the money landed, and a loop
                  that says "settled" beside a status that says "unpaid" is the
                  contradiction this column exists to remove. */}
              <td>
                {(() => {
                  if (!inv && !settled) return <span style={{ color: '#a0a8ae' }}>—</span>;
                  const stage = invoiceColumn(inv);
                  const isPaid = settled || stage === 'paid';
                  const tone = isPaid ? COLUMN_TONE.paid : (COLUMN_TONE[stage] || COLUMN_TONE.sent);
                  const why = !inv
                    ? 'Payment confirmed on this loop; the invoice record is gone'
                    : (settled && stage !== 'paid'
                      ? `Payment confirmed on this loop — the invoice itself still reads "${stage}"`
                      : `Invoice stage: ${stage}`);
                  return (
                    <span
                      title={why}
                      style={{ background: tone.bg, color: tone.fg, padding: '1px 8px', borderRadius: 10, fontSize: 11, whiteSpace: 'nowrap' }}
                    >
                      {isPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  );
                })()}
              </td>
              <td>{brandName(r.brand_id) || '—'}</td>
              <td style={{ fontSize: 12 }}>{(r.toAddresses || []).join(', ') || <span style={{ color: '#a15c00' }}>no recipient</span>}</td>
              <td style={{ fontSize: 12 }} title={r.startedAt ? 'First switched on' : (r.sendingEnabledAt ? 'Last switched on — this loop predates the start-date field' : 'Never switched on')}>
                {/* The day the chase began. Falls back to sendingEnabledAt for
                    loops that started before this field existed: that is the
                    best answer available for them, and showing "not started"
                    for a loop that has plainly sent would be worse than
                    approximate. A loop never switched on has not started, and
                    the day somebody drafted it is not a start date. */}
                {r.startedAt || r.sendingEnabledAt
                  ? fmtCell(r.startedAt || r.sendingEnabledAt)
                  : waiting
                    // Not started, but not undecided either — somebody chose
                    // when, and that is the useful answer here.
                    ? <span style={{ color: '#8a5a00' }} title="Scheduled start — switching Sending on holds until then">
                      set for {fmtInZone(r.scheduledStartAt, r.scheduleTimeZone)}
                    </span>
                    : <span style={{ color: '#a0a8ae' }}>not started</span>}
              </td>
              <td>
                {r.remindersSent || 0}
                {(r.ladder === 'original' || r.ladder === 'trade') && (
                  <span
                    title={LADDER_OPTIONS.find((o) => o.value === r.ladder).label}
                    style={{ marginLeft: 6, background: '#fff4e0', color: '#8a5a00', padding: '0 6px', borderRadius: 8, fontSize: 10, whiteSpace: 'nowrap' }}
                  >{r.ladder} ladder</span>
                )}
              </td>
              <td style={{ fontSize: 12 }}>
                {on
                  ? fmtInZone(r.nextReminderAt, r.scheduleTimeZone)
                  : waiting
                    ? <span style={{ color: '#8a5a00' }} title="Only once Sending is switched on">{fmtInZone(r.scheduledStartAt, r.scheduleTimeZone)} (when on)</span>
                    : '—'}
              </td>
              <td>{r.classification && r.classification !== 'unknown' ? r.classification.replace(/_/g, ' ') : '—'}</td>
              <td>{r.recoveredAmount ? round2(r.recoveredAmount) : '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onPreview(r)}>Review</button>
                {canConfirm && !settled && !archived && (
                  <button className="acc-btn ms-1" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onConfirm(r)}>Confirm payment</button>
                )}
                {onArchive && (
                  <button
                    className="acc-btn ms-1"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    disabled={busyId === r._id}
                    title={archived
                      ? 'Put it back on the Reminders board, exactly as it was'
                      : 'Take it off the board and stop it sending — nothing is lost'}
                    onClick={() => onArchive(r, !archived)}
                  >
                    {archived ? 'Restore' : 'Archive'}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ────────────────────────────────────────────────────────────── Analytics
function AnalyticsTab({ brandName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    (async () => {
      try { const r = await getInvoiceAnalytics(); setData((r && r.data) || null); }
      catch (e) { setError((e && e.message) || "Failed to load analytics"); }
    })();
  }, []);
  if (error) return <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>;
  if (!data) return <div className="cm-empty">Loading analytics…</div>;
  const byBrand = data.byBrand || [];
  return (
    <div className="invoice-analytics">
      <div className="metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
        <div className="metric"><span>{data.totalDue}</span><label>Total money due</label></div>
        <div className="metric"><span>{data.moneyRecovered}</span><label>Money recovered</label></div>
        <div className="metric"><span>{data.invoicesSent}</span><label>Invoices sent</label></div>
        <div className="metric"><span>{data.remindersSent}</span><label>Reminders sent</label></div>
        <div className="metric"><span>{data.responsesReceived}</span><label>Responses received</label></div>
        <div className="metric"><span>{data.responses && data.responses.positive}</span><label>Likely to pay</label></div>
        <div className="metric"><span>{data.responses && data.responses.negative}</span><label>Unwilling / unresponsive</label></div>
        <div className="metric"><span>{data.avgTimeToPayDays}</span><label>Avg days to pay</label></div>
      </div>

      <h5 style={{ fontSize: 15, marginTop: 22 }}>Payout per brand</h5>
      <p style={{ fontSize: 12, color: '#888' }}>
        What each brand billed, what actually landed, and what is still out.
        “Recovered” is the part the reminder loops brought in — an invoice paid
        without any chasing counts as paid, not as recovered.
      </p>
      {byBrand.length === 0 && <div className="cm-empty">No invoices yet.</div>}
      {byBrand.length > 0 && (
        <table className="cm-table">
          <thead>
            <tr><th>Brand</th><th>Invoices</th><th>Invoiced</th><th>Paid</th><th>Outstanding</th><th>Recovered by loops</th><th>Reminders</th></tr>
          </thead>
          <tbody>
            {byBrand.map((b) => (
              <tr key={b.brand_id || 'none'}>
                <td>{brandName(b.brand_id) || (b.brand_id ? b.brand_id : 'Company-wide')}</td>
                <td>{b.invoices}</td>
                <td>{fmtMoney(b.invoiced, b.currency)}</td>
                <td style={{ color: '#1e6b3e' }}>{fmtMoney(b.paidAmount, b.currency)}</td>
                <td style={{ color: b.outstanding ? '#a15c00' : '#888' }}>{fmtMoney(b.outstanding, b.currency)}</td>
                <td>{fmtMoney(b.recovered, b.currency)}</td>
                <td>{b.remindersSent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Page
export default function InvoiceManagement() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux   = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux     = useSelector((s) => s.user.allPermission);
  const FinalPermissions = useMemo(() => (
    (allPermissionRedux || [])
      .filter((p) => (companyPermissionRedux || []).includes(p))
      .filter((p) => (usersPermissionRedux || []).includes(p))
  ), [allPermissionRedux, companyPermissionRedux, usersPermissionRedux]);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const has = (perm) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[perm]);

  const { tab: urlTab } = useParams();
  const history = useHistory();
  const visibleTabs = TABS.filter((t) => has(t.perm));
  const tab = (TABS.find((t) => t.key === urlTab) ? urlTab : (visibleTabs[0]?.key || 'invoices'));
  const setTab = (k) => history.push(`/invoice-management/${k}`);

  const [rows, setRows] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsErr, setAccountsErr] = useState('');
  // brand_id → the mailbox that brand is remembered to bill from. Comes down
  // with the list so the create form can preselect without a round trip per
  // brand.
  const [brandDefaults, setBrandDefaults] = useState({});
  const [archivedInvoices, setArchivedInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  // The invoice being corrected. The same form writes and edits, so this is the
  // whole of "edit mode" at this level.
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [remindFor, setRemindFor] = useState('');
  const [fromTime, setFromTime] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  // The invoice whose document is open. Reading, never writing — it is held
  // separately from `editing` so opening the document cannot be mistaken for
  // starting a correction.
  const [viewingDoc, setViewingDoc] = useState(null);
  const [togglingId, setTogglingId] = useState('');
  // Board or table. Remembered, because an operator who prefers one prefers it
  // every time.
  const [view, setView] = useState(() => {
    try { return window.localStorage.getItem('im.invoiceView') || 'kanban'; } catch { return 'kanban'; }
  });
  const pickView = (v) => {
    setView(v);
    try { window.localStorage.setItem('im.invoiceView', v); } catch { /* private mode */ }
  };

  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  const brandName = useCallback((id) => {
    if (!id) return '';
    const b = (brands || []).find((x) => String(x._id || x.id) === String(id));
    return (b && b.name) || '';
  }, [brands]);
  const visibleRows = useMemo(() => applySystemFilter(rows, filters), [rows, filters]);
  const invoiceOf = useCallback(
    (id) => (allInvoices.length ? allInvoices : rows).find((i) => String(i._id) === String(id)),
    [allInvoices, rows],
  );

  // Archiving is reversible and says what it did, because archiving a flow
  // that is still switched on is the case where somebody could otherwise
  // expect the mail to keep going.
  const toggleArchive = async (row, archived) => {
    if (archived && row.sendingEnabled && !window.confirm(
      'This loop is still switched ON. Archiving it stops the reminders as well as hiding it.\n\nArchive it?'
    )) return;
    setTogglingId(row._id);
    try {
      await archiveReminder(row._id, archived, '');
      await refresh();
    } catch (e) { setErr((e && e.message) || (archived ? 'Could not archive it' : 'Could not restore it')); }
    finally { setTogglingId(null); }
  };

  // An invoice goes away with its loop — leaving the loop running is exactly
  // how an archived invoice would still be chasing somebody.
  const toggleInvoiceArchive = async (inv, archived) => {
    if (archived && !window.confirm(
      `Archive invoice ${inv.invoiceNumber || ''}?\n\nIt comes off the board and its reminder loop is archived with it. Nothing is deleted.`
    )) return;
    setTogglingId(inv._id);
    try {
      const r = await archiveInvoice(inv._id, archived);
      const n = (r && r.data && r.data.loopsArchived) || 0;
      if (archived && n) setNote(`Archived, along with ${n} reminder loop${n === 1 ? '' : 's'}.`);
      await refresh();
    } catch (e) { setErr((e && e.message) || 'Could not archive it'); }
    finally { setTogglingId(null); }
  };

  const refresh = useCallback(async () => {
    if (SELF_CONTAINED.has(tab)) return;
    setBusy(true); setErr(null);
    try {
      const isReminderTab = tab === 'reminders' || tab === 'archive';
      const r = isReminderTab
        ? await listReminders(tab === 'archive' ? { archived: 'true' } : {})
        : await listInvoices();
      setRows(((r && r.data) || []));
      // The Archive tab lists put-away INVOICES under the put-away loops: an
      // invoice is archived with its loop, so looking for one and finding only
      // the other would be half an answer.
      if (tab === 'archive') {
        const ai = await listInvoices(null, { archived: 'true' }).catch(() => null);
        setArchivedInvoices((ai && ai.data) || []);
      }
      // The reminders table names each invoice, so it needs them both.
      if (isReminderTab) {
        const inv = await listInvoices().catch(() => null);
        setAllInvoices((inv && inv.data) || []);
      }
    } catch (e) { setErr((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setBusy(false); }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  // Clients and mailboxes feed the pickers in both create paths and the
  // reminder form; loaded once rather than per modal.
  useEffect(() => {
    (async () => {
      try { const r = await listClients(); setClients((r && r.data) || []); } catch (_) { /* pickers show empty */ }
      // Invoice-gated, so the picker works for a user with the invoicing
      // package but not the email one. It used to read /api/email/accounts and
      // a 403 there left the "Send from" dropdown empty with nothing on screen
      // saying why.
      try {
        const r = await listReminderMailboxes();
        setAccounts((r && r.data) || []);
        setBrandDefaults((r && r.brandDefaults) || {});
        setAccountsErr('');
      } catch (e) {
        // Never silent again: an empty picker with no explanation is the thing
        // that made this hard to see in the first place.
        setAccounts([]);
        setBrandDefaults({});
        setAccountsErr((e && e.message) || 'Could not load your mailboxes');
      }
    })();
  }, []);

  const loadInvoiceOptions = useCallback(async () => {
    try { const r = await listInvoices(); setAllInvoices((r && r.data) || []); }
    catch (_) { /* picker just shows empty */ }
  }, []);

  if (visibleTabs.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Your company doesn't have the Invoice Management package enabled. Ask a super admin to add it.
        </div>
      </div>
    );
  }

  const canCreateInvoice  = has('INVOICE_ADD');
  const canCreateReminder = has('INVOICE_REMINDER_ADD');
  const canConfirm        = has('INVOICE_PAYMENT_CONFIRM');

  // The checkbox. Ticking it stops every loop chasing this invoice, so it says
  // so before it does it.
  const markPaid = async (inv, paid) => {
    setErr(null); setNote('');
    if (paid && !window.confirm(
      `Mark ${inv.invoiceNumber || 'this invoice'} paid?\n\nEvery reminder loop chasing it stops, and no further email about it will be sent.`
    )) return;
    try {
      const r = await setInvoicePaid(inv._id, paid);
      const stopped = (r && r.stoppedFlows) || 0;
      setNote(paid
        ? `${inv.invoiceNumber || 'Invoice'} marked paid${stopped ? ` — ${stopped} loop${stopped === 1 ? '' : 's'} stopped` : ''}.`
        : `${inv.invoiceNumber || 'Invoice'} reopened. Loops stay off until you switch one back on.`);
      await refresh();
    } catch (e) { setErr((e && e.message) || 'Could not change the invoice'); }
  };

  // Turning a loop on IS the send, and the confirm says that in those words.
  const toggleSending = async (r, enabled) => {
    setErr(null); setNote('');
    const inv = invoiceOf(r.invoice_id);
    // The confirm has to describe the act that is actually about to happen.
    // "The first email goes within a minute" over a loop scheduled for the 15th
    // is a warning about something that will not occur, and an operator who
    // reads it and cancels has been talked out of the right decision.
    const scheduled = startIsPending(r.scheduledStartAt) ? new Date(r.scheduledStartAt) : null;
    if (enabled && !window.confirm(
      `Start sending reminders for ${inv ? inv.invoiceNumber : 'this invoice'}?\n\n`
      + (scheduled
        ? `Nothing is emailed yet. The first email goes to ${(r.toAddresses || []).join(', ') || 'the client'} on `
          + `${fmtInZone(scheduled, r.scheduleTimeZone)}, `
        : `The first email goes to ${(r.toAddresses || []).join(', ') || 'the client'} within a minute, `)
      + `then ${(((LADDER_OPTIONS.find((o) => o.value === (r.ladder || 'standard')) || LADDER_OPTIONS[0]).schedule) || '').replace(/^./, (c) => c.toLowerCase())} It stops when the invoice is marked paid.`
    )) return;
    setTogglingId(r._id);
    try {
      // The server is the authority on which of the two happened — it compares
      // the start against its own clock, and the browser's may disagree.
      const res = await setReminderSending(r._id, enabled);
      const armedFor = res && res.armedFor;
      setNote(!enabled
        ? 'Sending is off. Nothing further will be emailed.'
        : armedFor
          ? `Armed. Nothing has been emailed — the first reminder goes out ${new Date(armedFor).toLocaleString()}.`
          : 'Sending is on — the first reminder goes on the next tick.');
      await refresh();
    } catch (e) { setErr((e && e.message) || 'Could not change the loop'); }
    finally { setTogglingId(''); }
  };

  const confirm = async (r) => {
    if (!window.confirm('Confirm this invoice was paid? This stops the reminder loop.')) return;
    try { await confirmPayment(r._id); await refresh(); }
    catch (e) { setErr((e && e.message) || 'Could not confirm payment'); }
  };

  const openReminder = (invoiceId) => { loadInvoiceOptions(); setRemindFor(invoiceId || ''); setReminding(true); };

  return (
    <div className="container py-4">
      <style>{`
        .im-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 16px; }
        .im-tab { padding: 8px 14px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 13px; color: #525252; }
        .im-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .cm-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; }
        .cm-table { width: 100%; font-size: 13px; }
        .cm-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .cm-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; }
        .cm-empty { color: #999; padding: 16px; text-align: center; }
        .invoice-analytics .metric { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 12px; }
        .invoice-analytics .metric span { display: block; font-size: 22px; font-weight: 700; color: #15172b; }
        .invoice-analytics .metric label { font-size: 11px; color: #888; }
        .im-view { border: 1px solid #d8dce0; background: #fff; color: #5b6670; font-size: 12px; padding: 3px 12px; cursor: pointer; }
        .im-view.active { background: #15172b; color: #fff; border-color: #15172b; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h3 className="mb-0">Invoice Management</h3>
        {tab === 'invoices' && canCreateInvoice && (
          <span>
            <button className="acc-btn" onClick={() => setFromTime(true)}>From time entries</button>
            <button className="acc-btn ms-2" onClick={() => setUploading(true)}>Upload invoice</button>
            <button className="acc-btn acc-btn-primary ms-2" onClick={() => setCreating(true)}>+ New invoice</button>
          </span>
        )}
        {tab === 'reminders' && canCreateReminder && (
          <button className="acc-btn acc-btn-primary" onClick={() => openReminder('')}>+ Set reminder</button>
        )}
      </div>

      <div className="im-tabs">
        {visibleTabs.map((t) => (
          <div key={t.key} className={"im-tab" + (tab === t.key ? " active" : "")} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      {!SELF_CONTAINED.has(tab) && (
        <SystemFilterBar
          filters={filters}
          setFilters={setFilters}
          brands={brands}
          resultCount={visibleRows.length}
          totalCount={rows.length}
        />
      )}

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {note && <div className="alert alert-info" style={{ fontSize: 13 }}>{note}</div>}

      {tab === 'invoices' && (
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div style={{ fontSize: 12, color: '#888' }}>
            Tick <strong>Paid</strong> (or drag a card into Paid) to settle an invoice — every loop chasing it stops and nothing more is emailed about it.
          </div>
          <div>
            <button className={'im-view' + (view === 'kanban' ? ' active' : '')} style={{ borderRadius: '12px 0 0 12px' }} onClick={() => pickView('kanban')}>Kanban</button>
            <button className={'im-view' + (view === 'list' ? ' active' : '')} style={{ borderRadius: '0 12px 12px 0' }} onClick={() => pickView('list')}>List</button>
          </div>
        </div>
      )}

      <div className="cm-card">
        {tab === 'analytics' && <AnalyticsTab brandName={brandName} />}
        {tab === 'clients' && <ClientsTab can={has} brands={brands} />}
        {tab === 'time' && <TimeTab can={has} />}
        {tab === 'banking' && <BankAccountsTab can={has} brands={brands} />}
        {tab === 'email' && <EmailSetupTab can={has} brands={brands} />}

        {!SELF_CONTAINED.has(tab) && (
          <>
            {busy && rows.length === 0 && <div className="cm-empty">Loading…</div>}
            {!busy && rows.length === 0 && (
              <div className="cm-empty">
                {tab === 'archive'
                  ? (archivedInvoices.length
                    ? 'No archived reminder loops — the archived invoices are listed above.'
                    : 'Nothing archived. Archiving takes an invoice or a loop off the board without losing what it billed or chased.')
                  : tab === 'reminders'
                    ? 'No reminder loops yet. Start one from an invoice — it is created switched off.'
                    : 'No invoices yet. Build one from line items, or upload a PDF you already have.'}
              </div>
            )}
            {!busy && rows.length > 0 && visibleRows.length === 0 && <div className="cm-empty">No {tab} match the current filters.</div>}

            {visibleRows.length > 0 && tab === 'invoices' && (
              view === 'kanban'
                ? <InvoiceBoard rows={visibleRows} brandName={brandName} canConfirm={canConfirm} canRemind={canCreateReminder} canEdit={has('INVOICE_EDIT')} onPaid={markPaid} onRemind={(inv) => openReminder(inv._id)} onEdit={setEditing} onView={setViewingDoc} />
                : <InvoiceList rows={visibleRows} brandName={brandName} canConfirm={canConfirm} canRemind={canCreateReminder} canEdit={has('INVOICE_EDIT')} onPaid={markPaid} onRemind={(inv) => openReminder(inv._id)} onEdit={setEditing} onView={setViewingDoc} onArchive={toggleInvoiceArchive} busyId={togglingId} />
            )}

            {/* Archived INVOICES, above the archived loops. An invoice is put
                away with its loop, so a tab that showed only one of them would
                answer half the question. */}
            {tab === 'archive' && archivedInvoices.length > 0 && (
              <>
                <h6 style={{ fontSize: 13, margin: '4px 0 8px' }}>Archived invoices</h6>
                <InvoiceList
                  rows={archivedInvoices}
                  brandName={brandName}
                  canConfirm={false}
                  canRemind={false}
                  onPaid={() => {}}
                  onRemind={() => {}}
                  onView={setViewingDoc}
                  archived
                  onArchive={toggleInvoiceArchive}
                  busyId={togglingId}
                />
                <h6 style={{ fontSize: 13, margin: '18px 0 8px' }}>Archived reminder loops</h6>
              </>
            )}

            {visibleRows.length > 0 && (tab === 'reminders' || tab === 'archive') && (
              <ReminderList
                rows={visibleRows}
                invoiceOf={invoiceOf}
                brandName={brandName}
                canConfirm={canConfirm}
                busyId={togglingId}
                onToggle={toggleSending}
                onPreview={setPreviewing}
                onConfirm={confirm}
                archived={tab === 'archive'}
                onArchive={toggleArchive}
              />
            )}
          </>
        )}
      </div>

      {creating && (
        <CreateInvoiceModal
          brands={brands}
          clients={clients}
          accounts={accounts}
          accountsErr={accountsErr}
          brandDefaults={brandDefaults}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
          // The board updates behind the compiled preview, so the invoice is
          // never "missing" while it is being looked at.
          onRefresh={refresh}
        />
      )}
      {editing && (
        <CreateInvoiceModal
          // Keyed by invoice so opening a second one re-seeds the form rather
          // than showing the first one's values in it.
          key={editing._id}
          invoice={editing}
          brands={brands}
          clients={clients}
          accounts={accounts}
          accountsErr={accountsErr}
          brandDefaults={brandDefaults}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
          onRefresh={refresh}
        />
      )}
      {uploading && (
        <UploadInvoiceModal
          brands={brands}
          clients={clients}
          onClose={() => setUploading(false)}
          onSaved={() => { setUploading(false); refresh(); }}
        />
      )}
      {fromTime && (
        <FromTimeEntriesModal
          onClose={() => setFromTime(false)}
          onCreated={() => { setFromTime(false); refresh(); }}
        />
      )}
      {/* THE INVOICE ITSELF. Built or uploaded, one panel: the operator asked
          "what does this bill?", not "how was it made?". Keyed by invoice so
          opening a second one refetches instead of showing the first. */}
      {viewingDoc && (
        <DocumentViewer
          key={viewingDoc._id}
          title={`Invoice ${viewingDoc.invoiceNumber || ''}`.trim()}
          note={viewingDoc.source === 'uploaded' ? 'the PDF uploaded for this invoice' : 'compiled from its line items'}
          load={() => fetchInvoiceDocument(viewingDoc._id)}
          onClose={() => setViewingDoc(null)}
        />
      )}
      {previewing && (
        <ReminderPreview
          reminder={previewing}
          invoices={allInvoices.length ? allInvoices : rows}
          onClose={() => setPreviewing(null)}
          onChanged={refresh}
        />
      )}
      {reminding && (
        <SetReminderModal
          invoices={allInvoices}
          accounts={accounts}
          accountsErr={accountsErr}
          brandName={brandName}
          preselect={remindFor}
          onClose={() => { setReminding(false); setRemindFor(''); }}
          onSaved={() => {
            setReminding(false); setRemindFor('');
            setNote('Loop ready and switched OFF, on the Reminders tab. Turn on Sending when you want it to start.');
            refresh();
          }}
        />
      )}
    </div>
  );
}
